// Feed — the cortex-WIDE Slack knowledge surface (sits next to Board in the
// nav; both are cross-project by construction).
//
// Two tabs over the feed backend (backend/modules/feed):
//   Digests  — LLM-grouped TOPICS from the collected group channels. A topic
//              confidently about one project is auto-routed into that
//              project's Docs → Knowledge (type 'digest'); the rest stays
//              here — cortex-level knowledge is a first-class destination,
//              not a triage buffer. Unsure suggestions get one-click routing.
//   Channels — the strictly OPT-IN listening registry: nothing is collected
//              until a channel is toggled on. Re-enabling backfills from the
//              frozen cursor (capped at 30 days) or "new only".
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  CircleCheck,
  Copy,
  EyeOff,
  Hash,
  Info,
  KeyRound,
  Loader2,
  Lock,
  MessageCircle,
  MessagesSquare,
  Mic,
  RefreshCw,
  Rss,
  Send,
  Settings as SettingsIcon,
  Share2,
  ShieldCheck,
  Trash2,
  Upload,
  Users,
  Webhook,
  X,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { fetchData } from "../fetchapi/fetchapi";
import { listProjects } from "../services/projects";
import { useAuth } from "../contexts/AuthContext";
import { createAppRequest } from "../services/apps";
import { btnPrimary, btnGhost } from "../components/board/requestMeta";

// btnPrimary/btnGhost come from the shared form tokens in requestMeta — one
// visual language across every task window (and the rest of this page).
const selectCls = "rounded-md border border-card-border bg-card-bg text-page-text text-sm px-2 py-1.5 cursor-pointer";

const MD_COMPONENTS = {
  a: (p) => <a {...p} target="_blank" rel="noreferrer" className="text-nav-active underline underline-offset-2" />,
  p: (p) => <p {...p} className="text-sm text-page-text leading-relaxed my-1.5" />,
  ul: (p) => <ul {...p} className="list-disc pl-5 my-1.5 space-y-0.5 text-sm text-page-text" />,
  ol: (p) => <ol {...p} className="list-decimal pl-5 my-1.5 space-y-0.5 text-sm text-page-text" />,
  li: (p) => <li {...p} className="text-sm text-page-text" />,
  h1: (p) => <h3 {...p} className="text-sm font-semibold text-page-title mt-2 mb-1" />,
  h2: (p) => <h3 {...p} className="text-sm font-semibold text-page-title mt-2 mb-1" />,
  h3: (p) => <h4 {...p} className="text-sm font-semibold text-page-title mt-2 mb-1" />,
  code: (p) => <code {...p} className="text-[12px] bg-table-stripe rounded px-1 py-0.5" />,
  blockquote: (p) => <blockquote {...p} className="border-l-2 border-card-border pl-3 text-page-muted my-1.5" />,
  // Tables (a summary's "who does what by when") fell through to the browser
  // default and rendered a size larger than the prose around them.
  table: (p) => <div className="my-1.5 overflow-x-auto"><table {...p} className="w-full text-sm text-page-text border-collapse" /></div>,
  thead: (p) => <thead {...p} className="text-left text-page-muted" />,
  th: (p) => <th {...p} className="text-sm font-semibold text-page-title px-2 py-1 border-b border-card-border align-top" />,
  td: (p) => <td {...p} className="text-sm text-page-text px-2 py-1 border-b border-page-divider align-top" />,
  tr: (p) => <tr {...p} />,
};

// ── PRIVACY (backend/modules/feed/privacy.js) ───────────────────────────────
// A redacted transcript carries one marker line per hidden stretch:
// [[redacted:<category>:<n lines>]]. The reader learns that something was
// there and what kind — never what it said.
const MARK_RE = /^\[\[redacted:([a-z_]+)(?::(\d+))?\]\]$/;
const PRIVACY_LABELS = { personal: "personal chatter", finance: "money & commercial terms", hr: "people & HR" };
const LEVEL_META = [
  { id: "hidden", label: "Hidden everywhere", hint: "cut from the transcript, the summary, the topics and every project copy" },
  { id: "transcript", label: "Transcript only", hint: "readable in the Feed transcript; cut from the summary, the topics and every project copy" },
  { id: "visible", label: "Visible everywhere", hint: "kept in the transcript, the summary and what travels into projects" },
];

function TranscriptView({ text, className = "" }) {
  const lines = String(text || "").split("\n");
  return (
    <div className={`text-xs text-page-text bg-table-stripe rounded-md p-3 max-h-72 overflow-y-auto whitespace-pre-wrap ${className}`}>
      {lines.map((l, i) => {
        const mk = MARK_RE.exec(l.trim());
        if (mk) {
          const n = mk[2] ? Number(mk[2]) : null;
          return (
            <div key={i} className="my-1 inline-flex items-center gap-1 rounded bg-card-bg border border-card-border px-2 py-0.5 text-[11px] text-page-muted"
              title="Hidden by the owner's privacy settings — nobody but the owner can read this part">
              <EyeOff size={11} strokeWidth={2.5} /> {PRIVACY_LABELS[mk[1]] || mk[1]} hidden{n ? ` · ${n} line${n === 1 ? "" : "s"}` : ""}
            </div>
          );
        }
        return <div key={i}>{l}</div>;
      })}
    </div>
  );
}

// The projects a topic is in — one green pill per route, with a plain
// "remove" link (not a bare ×: a cross next to a project name read as a state
// rather than an action, 2026-09-08). Remove takes the topic out of THAT
// project: Knowledge page deleted, requirement source retired.
function RouteChips({ routes, canManage, onRemove, busy }) {
  if (!routes?.length) return null;
  return (
    <>
      {routes.map((r) => (
        <span key={r.project_slug}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-[11px] font-medium shrink-0"
          title={`This topic is in project ${r.project_slug}${r.auto ? " — sent automatically" : ""}${r.routed_by ? ` by ${r.routed_by}` : ""}`}>
          <CircleCheck size={12} strokeWidth={2.5} /> In {r.project_slug}
          {canManage && (
            <button type="button" disabled={busy === r.project_slug} onClick={() => onRemove(r.project_slug)}
              className="ml-0.5 border-0 bg-transparent p-0 cursor-pointer text-[11px] underline underline-offset-2 text-emerald-700/80 dark:text-emerald-400/80 hover:text-emerald-700 dark:hover:text-emerald-300"
              title={`Take this topic out of ${r.project_slug} (its Knowledge page is deleted, its requirement source retired)`}>
              {busy === r.project_slug ? <Loader2 size={10} className="animate-spin inline" /> : "remove"}
            </button>
          )}
        </span>
      ))}
    </>
  );
}

// ONE meeting topic row. Three states, each said in one sentence:
//   not sent   — "Not in any project yet · looks like X (95%)" + [Send to X]
//   sent       — green "In X" pill(s) with "remove"
//   dismissed  — struck through, [Restore]
// The topic — not the call — is what a project receives (its summary + its
// own redacted excerpt).
function MeetingTopicRow({ meetingId, topic: t, projects, canManage, onChanged, spec }) {
  const routes = t.routes || [];
  const suggested = t.project_slug && !routes.some((r) => r.project_slug === t.project_slug) ? t.project_slug : null;
  // Preselect the best-matching project; nothing when the call matched none.
  const [target, setTarget] = useState(suggested || "");
  const [busy, setBusy] = useState(null); // 'route' | project slug (remove) | 'dismiss'
  const [open, setOpen] = useState(false);
  const pct = t.confidence != null ? Math.round(t.confidence * 100) : null;
  const projectName = (slug) => projects.find((p) => p.slug === slug)?.name || slug;
  const post = async (path, body) => fetchData(`meetings/${meetingId}/topics/${t.id}/${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
  });
  const send = async (slug) => {
    if (!slug) { toast.error("pick a project first"); return; }
    if (routes.some((r) => r.project_slug === slug)) { toast("already in that project"); return; }
    setBusy("route");
    try {
      await post("route", { project_slug: slug });
      toast.success(`"${t.title}" is now in ${projectName(slug)}: Knowledge page + requirement source added, requirements review scheduled`);
      setTarget("");
      onChanged();
    } catch (e) { toast.error(String(e?.message || "could not send")); }
    finally { setBusy(null); }
  };
  const remove = async (slug) => {
    setBusy(slug);
    try {
      const r = await post("unroute", { project_slug: slug });
      toast.success(`removed from ${projectName(slug)}${r.problems?.length ? " (some cleanup pending)" : ""}`);
      onChanged();
    } catch (e) { toast.error(String(e?.message || "could not remove")); }
    finally { setBusy(null); }
  };
  const dismiss = async (dismissed) => {
    setBusy("dismiss");
    try { await post("dismiss", { dismissed }); onChanged(); }
    catch (e) { toast.error(String(e?.message || "could not update the topic")); }
    finally { setBusy(null); }
  };
  if (t.status === "dismissed") {
    return (
      <div className="flex items-center gap-2 text-page-muted" data-testid={`meeting-topic-${t.id}`}>
        <span className="select-none leading-6">•</span>
        <span className="min-w-0 flex-1 text-sm leading-6 line-through opacity-60">{t.title}</span>
        {canManage && <button type="button" className={btnGhost + " shrink-0"} disabled={busy === "dismiss"} onClick={() => dismiss(false)}>Restore</button>}
      </div>
    );
  }
  const sent = routes.length > 0;
  return (
    <div className="group rounded-md border border-card-border/70 p-2.5 space-y-1.5" data-testid={`meeting-topic-${t.id}`}>
      {/* Line 1: the subject, and where it is. */}
      <div className="flex items-start gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="min-w-0 flex-1 text-left bg-transparent border-0 p-0 cursor-pointer">
          <span className="inline-flex items-center gap-1 text-sm font-medium text-page-title">
            <ChevronRight size={13} strokeWidth={2.5} className={`transition-transform ${open ? "rotate-90" : ""}`} />
            {t.title}
          </span>
        </button>
        <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
          {sent ? (
            <RouteChips routes={routes} canManage={canManage} onRemove={remove} busy={busy} />
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-medium"
              title={t.route_error ? `Automatic send failed: ${t.route_error}` : "This topic has not been sent to any project"}>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
              Not in any project yet
              {suggested && <span className="font-normal opacity-90">· looks like {projectName(suggested)}{pct != null ? ` (${pct}%)` : ""}</span>}
              {t.route_error && <span className="font-normal">· automatic send failed</span>}
            </span>
          )}
        </div>
      </div>
      {open && (
        <div className="pl-4">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{t.summary_md || "_no summary_"}</ReactMarkdown>
        </div>
      )}
      {/* Line 2: ONE select (the best-matching project preselected, empty when
          nothing matched) + ONE Send, disabled until a project is chosen. Same
          height in every state. */}
      {canManage && (
        <div className="pl-4 flex flex-wrap items-center gap-2">
          <select value={target} onChange={(e) => { setTarget(e.target.value); if (e.target.value) spec?.ensure(e.target.value); }}
            className={selectCls + " text-xs h-8 w-56 truncate"} data-testid={`meeting-topic-project-${t.id}`}>
            <option value="">{sent ? "another project…" : "choose a project…"}</option>
            {projects.filter((p) => !routes.some((r) => r.project_slug === p.slug))
              .map((p) => <option key={p.slug} value={p.slug}>{p.name || p.slug}</option>)}
          </select>
          <button type="button" className={btnGhost + " shrink-0 h-8"} disabled={busy === "route" || !target} onClick={() => send(target)}
            title={target ? `Send this topic to ${projectName(target)}: Docs → Knowledge page + Requirement source (only this topic's summary and its redacted excerpt travel)` : "Choose a project first"}
            data-testid={`meeting-topic-send-${t.id}`}>
            {busy === "route" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} strokeWidth={2.5} />} Send
          </button>
          <SpecChip slug={target} spec={spec} />
          {!sent && (
            <button type="button" disabled={busy === "dismiss"} onClick={() => dismiss(true)}
              className="ml-auto border-0 bg-transparent p-0 cursor-pointer text-[11px] text-page-muted underline underline-offset-2 hover:text-page-text"
              title="Hide this topic — it is not worth keeping (can be restored)" data-testid={`meeting-topic-dismiss-${t.id}`}>
              not a topic
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// One automatic-send switch row (Privacy tab).
function AutoRouteRow({ label, hint, value, busy, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-2" data-testid={testid}>
      <div className="min-w-[200px] flex-1">
        <div className="text-sm text-page-title">{label}</div>
        <div className="text-[11px] text-page-muted">{hint}</div>
      </div>
      <Switch checked={value} disabled={busy} onCheckedChange={onChange} />
    </div>
  );
}

// Feed → Settings → Privacy. Two questions, kept apart:
//   WHAT STAYS HIDDEN — one row per category, TWO columns: Meetings (three
//   levels) and Slack (two — a chat has no transcript surface).
//   WHAT IS SENT TO PROJECTS AUTOMATICALLY — three switches, one per source.
// The same two blocks repeat per organization for org admins (the org's Slack
// channels + the default for members who set nothing).
const SLACK_LEVEL_META = [
  { id: "hidden", label: "Hidden", hint: "left out of the digests entirely" },
  { id: "visible", label: "Visible", hint: "summarized like any other conversation" },
];

function PrivacySettings({ orgLabel = (s) => s }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [reapplying, setReapplying] = useState(false);
  const load = useCallback(async () => {
    try { setData(await fetchData("feed/privacy")); }
    catch (e) { toast.error(String(e?.message || "could not load privacy settings")); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-xs text-page-muted"><Loader2 size={12} className="animate-spin inline mr-1" />loading…</div>;
  const mineEff = { ...data.defaults, ...(data.mine || {}) };
  const put = async (who, key, value) => {
    setSaving(`${who}:${key}`);
    try {
      if (who === "me") {
        const r = await fetchData("feed/privacy", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ settings: { [key]: value } }) });
        setData((d) => ({ ...d, mine: r.mine, effective: r.effective }));
      } else {
        const r = await fetchData(`feed/privacy/org/${encodeURIComponent(who)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ settings: { [key]: value } }) });
        setData((d) => ({ ...d, orgs: d.orgs.map((o) => o.org_slug === who ? { ...o, settings: r.settings } : o) }));
      }
      toast.success(who === "me" ? "saved" : `saved for ${orgLabel(who)}`);
    } catch (e) { toast.error(String(e?.message || "could not save")); }
    finally { setSaving(null); }
  };
  const reapply = async () => {
    setReapplying(true);
    try {
      const r = await fetchData("feed/privacy/reapply", { method: "POST" });
      toast.success(`re-deriving ${r.meetings} meeting${r.meetings === 1 ? "" : "s"} under your current settings — transcripts, summaries, topics and project copies update in the background`);
    } catch (e) { toast.error(String(e?.message || "could not queue the re-apply")); }
    finally { setReapplying(false); }
  };
  // Segmented level picker; `meta` = LEVEL_META (meetings) or SLACK_LEVEL_META.
  const Levels = ({ value, meta, onPick, busy, testid }) => (
    <div className="inline-flex rounded-md border border-card-border overflow-hidden h-8" role="radiogroup" data-testid={testid}>
      {meta.map((lv) => (
        <button key={lv.id} type="button" role="radio" aria-checked={value === lv.id} title={lv.hint} disabled={!!busy}
          onClick={() => value !== lv.id && onPick(lv.id)}
          className={`px-2.5 text-xs border-0 cursor-pointer ${value === lv.id ? "bg-nav-active text-white" : "bg-card-bg text-page-text hover:bg-table-stripe"}`}>
          {busy ? <Loader2 size={11} className="animate-spin" /> : lv.label}
        </button>
      ))}
    </div>
  );
  // Slack's effective level: its own column, else the meetings level (where
  // "transcript only" means hidden — a chat has no transcript to show it in).
  const slackLevel = (settings, cat) => settings[`slack.${cat}`] || (settings[cat] === "visible" ? "visible" : "hidden");
  const HiddenTable = ({ settings, who }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-page-muted">
            <th className="py-1.5 pr-3 font-semibold">What</th>
            <th className="py-1.5 pr-3 font-semibold"><span className="inline-flex items-center gap-1"><Mic size={12} strokeWidth={2.5} /> Meetings</span></th>
            <th className="py-1.5 font-semibold"><span className="inline-flex items-center gap-1"><MessagesSquare size={12} strokeWidth={2.5} /> Slack</span></th>
          </tr>
        </thead>
        <tbody>
          {data.categories.map((c) => (
            <tr key={c.id} className="border-t border-page-divider">
              <td className="py-2 pr-3 align-top min-w-[180px]">
                <div className="text-sm text-page-title">{c.label}</div>
                <div className="text-[11px] text-page-muted">{c.hint}</div>
              </td>
              <td className="py-2 pr-3 align-top">
                <Levels value={settings[c.id] || "hidden"} meta={LEVEL_META} busy={saving === `${who}:${c.id}`}
                  onPick={(lv) => put(who, c.id, lv)} testid={`privacy-${who}-${c.id}`} />
              </td>
              <td className="py-2 align-top">
                <Levels value={slackLevel(settings, c.id)} meta={SLACK_LEVEL_META} busy={saving === `${who}:slack.${c.id}`}
                  onPick={(lv) => put(who, `slack.${c.id}`, lv)} testid={`privacy-${who}-slack-${c.id}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  const Section = ({ title, children }) => (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{title}</div>
      {children}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} strokeWidth={2.5} className="text-nav-active" />
          <span className="text-sm font-semibold text-page-title">My calls and chats</span>
        </div>
        <Section title="What stays hidden">
          <p className="text-xs text-page-muted mb-1">
            Meetings: <b>Hidden everywhere</b> cuts a stretch from the transcript others see (a marker stands in), the summary, the topics and
            every project copy; <b>Transcript only</b> keeps it readable in the Feed transcript but out of everything else; <b>Visible everywhere</b>
            lets it travel. Slack: <b>Hidden</b> leaves the conversation out of the digests, <b>Visible</b> summarizes it. The original words of a
            call are stored for you alone.
          </p>
          <HiddenTable settings={mineEff} who="me" />
        </Section>
        <Section title="What is sent to projects automatically">
          <p className="text-xs text-page-muted mb-1">
            A topic that clearly belongs to a project (≥ 75%) goes there by itself — Knowledge page, requirement source, requirements review,
            Board tasks — even when the source is private. Only the topic's summary and its redacted excerpt travel. Off: topics wait for “Send”.
          </p>
          <AutoRouteRow label="My meetings" hint="Topics of my calls." value={mineEff.auto_route_meetings !== false}
            busy={saving === "me:auto_route_meetings"} onChange={(v) => put("me", "auto_route_meetings", !!v)} testid="privacy-auto-route-meetings" />
          <AutoRouteRow label="My personal Slack chats" hint="Topics from my DMs and group chats." value={mineEff.auto_route_slack_personal !== false}
            busy={saving === "me:auto_route_slack_personal"} onChange={(v) => put("me", "auto_route_slack_personal", !!v)} testid="privacy-auto-route-slack-personal" />
          <div className="text-[11px] text-page-muted">Shared Slack channels are set per organization below (org admins).</div>
        </Section>
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-page-divider">
          <button type="button" className={btnGhost} disabled={reapplying} onClick={reapply} data-testid="privacy-reapply"
            title="Re-derive all your existing meetings under the current settings (no re-transcription)">
            {reapplying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} strokeWidth={2.5} />} Re-apply to my meetings
          </button>
          <span className="text-[11px] text-page-muted">New calls and chats use these settings automatically.</span>
        </div>
      </div>
      {(data.orgs || []).map((o) => {
        const eff = { ...data.defaults, ...(o.settings || {}) };
        return (
          <div key={o.org_slug} className="rounded-lg border border-card-border bg-card-bg p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Users size={16} strokeWidth={2.5} className="text-nav-active" />
              <span className="text-sm font-semibold text-page-title">Organization — {orgLabel(o.org_slug)}</span>
            </div>
            <p className="text-xs text-page-muted">The organization's shared Slack channels, and the default for members who have not set their own preference.</p>
            <Section title="What stays hidden"><HiddenTable settings={eff} who={o.org_slug} /></Section>
            <Section title="What is sent to projects automatically">
              <AutoRouteRow label="Shared Slack channels" hint="Topics from the organization's listened channels." value={eff.auto_route_slack_channels !== false}
                busy={saving === `${o.org_slug}:auto_route_slack_channels`} onChange={(v) => put(o.org_slug, "auto_route_slack_channels", !!v)}
                testid={`privacy-auto-route-channels-${o.org_slug}`} />
            </Section>
          </div>
        );
      })}
    </div>
  );
}

const fmtTs = (ts) => {
  const n = Number(ts);
  if (!n) return "—";
  return new Date(n * 1000).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

// Last-activity column: dates far in the past need the year to read sanely.
const fmtActivity = (ts) => {
  const n = Number(ts);
  if (!n) return "—";
  const d = new Date(n * 1000);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
};

const ACTIVE_WINDOW_SEC = 30 * 24 * 3600;
const CHANNEL_FILTERS = [
  { id: "all", label: "all channels" },
  { id: "listening", label: "listening" },
  { id: "active", label: "active (30d)" },
  { id: "quiet", label: "quiet" },
  { id: "gone", label: "archived / gone" },
];

// Manifest for a PERSONAL token (Feed → Settings): unlike the org collector's
// token, it includes im/mpim scopes — the whole point is the user's own DMs.
const TOKEN_MANIFEST = `display_information:
  name: cortex-my-slack
oauth_config:
  scopes:
    user:
      - channels:history
      - groups:history
      - im:history
      - mpim:history
      - channels:read
      - groups:read
      - im:read
      - mpim:read
      - users:read
settings:
  org_deploy_enabled: false
  socket_mode_enabled: false`;

// The ORG workspace token deliberately has NO im/mpim scopes — the org-level
// collector never touches anyone's DMs.
const ORG_TOKEN_MANIFEST = `display_information:
  name: cortex-knowledge-collector
oauth_config:
  scopes:
    user:
      - channels:history
      - groups:history
      - channels:read
      - groups:read
      - users:read
settings:
  org_deploy_enabled: false
  socket_mode_enabled: false`;

function TokenHelpDialog({ org = false }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" title="How to get a token"
          className="p-1.5 rounded-md border-0 bg-transparent text-page-muted hover:text-nav-active cursor-pointer">
          <Info size={16} strokeWidth={2.5} />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card-bg border-card-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-page-title">{org ? "How to get the workspace token" : "How to get your Slack token"}</DialogTitle>
          <DialogDescription className="text-page-muted">
            {org
              ? "Takes ~3 minutes on api.slack.com. This token reads the workspace's group channels only — no scopes for anyone's DMs."
              : "Takes ~3 minutes on api.slack.com. The token acts as YOU: it can only read chats you can read."}
          </DialogDescription>
        </DialogHeader>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-page-text">
          <li>Open <span className="font-medium">api.slack.com/apps</span> in a browser → <span className="font-medium">Create New App</span> → <span className="font-medium">From a manifest</span>.</li>
          <li>Pick your workspace, then paste this manifest (YAML):</li>
        </ol>
        <pre className="text-[11px] leading-snug bg-table-stripe rounded-md p-3 overflow-x-auto text-page-text">{org ? ORG_TOKEN_MANIFEST : TOKEN_MANIFEST}</pre>
        <ol start={3} className="list-decimal pl-5 space-y-1.5 text-sm text-page-text">
          <li>Create → on the app page open <span className="font-medium">OAuth & Permissions</span> → <span className="font-medium">Install to Workspace</span> → Allow.</li>
          <li>Copy the <span className="font-medium">User OAuth Token</span> (starts with <code className="bg-table-stripe rounded px-1">xoxp-</code>) and paste it here.</li>
        </ol>
        {!org && (
          <p className="text-xs text-page-muted">
            Your DMs stay visible only to you unless you explicitly share a chat on the My chats tab.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Same info-icon-opens-a-popup pattern as the Slack token cards: everything
// needed to wire Krisp up, without bloating the card itself.
function KrispHelpDialog({ urls = [] }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" title="How to connect Krisp"
          className="p-1.5 rounded-md border-0 bg-transparent text-page-muted hover:text-nav-active cursor-pointer">
          <Info size={16} strokeWidth={2.5} />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card-bg border-card-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-page-title">How to connect Krisp</DialogTitle>
          <DialogDescription className="text-page-muted">
            Krisp records any call made through its virtual devices (Slack huddles included) and
            pushes the transcript to cortex. Webhooks are available on Krisp's Core plan and up.
          </DialogDescription>
        </DialogHeader>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-page-text">
          <li>
            In the Krisp app open <span className="font-medium">Settings → Integrations</span>, find{" "}
            <span className="font-medium">Webhook</span> and choose <span className="font-medium">Connect</span>.
            Don't see it? Check your plan, or use the web app at <span className="font-medium">app.krisp.ai</span>.
          </li>
          <li>Give the webhook a name and paste {urls.length > 1 ? "the URL of the organization you are connecting" : "this URL"}:</li>
        </ol>
        {/* min-w-0 + wrap: the unbroken token URL must never set the dialog's
            width — it wraps inside its box instead. */}
        {urls.length ? urls.map(({ org, url }) => (
          <div key={org} className="min-w-0">
            {urls.length > 1 && <div className="text-[11px] font-medium text-page-muted mb-1">{org}</div>}
            <pre className="text-[11px] leading-snug bg-table-stripe rounded-md p-3 whitespace-pre-wrap break-all text-page-text">{url}</pre>
          </div>
        )) : (
          <pre className="text-[11px] leading-snug bg-table-stripe rounded-md p-3 whitespace-pre-wrap text-page-text">
            generate the webhook URL on this page first — it appears here
          </pre>
        )}
        <ol start={3} className="list-decimal pl-5 space-y-1.5 text-sm text-page-text">
          <li>
            Under Trigger Events select <span className="font-medium">only "Transcript is created"</span> —
            cortex ignores Krisp's generated notes and writes its own summary from the transcript.
          </li>
          <li>No request headers are needed (the URL itself is the secret). Click <span className="font-medium">Create</span>.</li>
          <li>
            On the webhook's <span className="font-medium">Configure</span> tab click{" "}
            <span className="font-medium">Send sample note</span> — the status pill here flips to
            "synced" as soon as the delivery arrives.
          </li>
        </ol>
        <p className="text-sm text-page-text">
          <span className="font-medium">API key (optional, recommended):</span> in Krisp open{" "}
          <span className="font-medium">Settings → Integrations → API → Create</span>, scope{" "}
          <span className="font-medium">Read</span>, copy the <code className="bg-table-stripe rounded px-1">krsp_u_…</code> key
          (shown once) and paste it here. It pulls on "Sync meetings" and hourly, and backfills the
          last 90 days of calls recorded before the webhook existed. The key sees the meetings of the
          user who created it. Connecting both is the intended setup — duplicates merge.
        </p>
        <p className="text-xs text-page-muted">
          To record Slack huddles, pick Krisp Microphone and Krisp Speaker as the audio devices in
          Slack's Preferences → Audio &amp; video. Summaries are written by cortex: personal chatter
          is cut, only the work discussion is kept.
        </p>
      </DialogContent>
    </Dialog>
  );
}

// ── Meetings ────────────────────────────────────────────────────────────────

const MEETING_ACTIVE = new Set(["uploading", "queued", "transcribing", "summarizing"]);


// A row that sat down in 'summarizing' WITH an error is a failed finish step
// (the words are safe server-side); it is not in flight any more.
const summaryFailed = (m) => m.status === "summarizing" && !!m.error;
const isMeetingActive = (m) => MEETING_ACTIVE.has(m.status) && !summaryFailed(m);

function MeetingStatusChip({ m }) {
  if (m.status === "done") return null;
  if (summaryFailed(m)) {
    return (
      <span title={m.error}
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-[var(--severity-warning-bg,#fef3c7)] text-[var(--severity-warning-text,#92400e)]">
        summary failed
      </span>
    );
  }
  // 'discarded' = the summary judged the call empty (hello/goodbye-only, mic
  // test). Only the uploader's own manual uploads ever reach the browser in
  // this state — feedback that the meeting was deliberately not added.
  if (m.status === "discarded") {
    return (
      <span title="The call had no work content (e.g. everyone just said hello and goodbye), so it was not added to the feed. You can delete this row."
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-table-stripe text-page-muted">
        no useful content — not added
      </span>
    );
  }
  const label = m.status === "transcribing" ? `transcribing ${m.progress ?? 0}%`
    : m.status === "failed" ? "failed" : m.status;
  const cls = m.status === "failed"
    ? "bg-[var(--severity-warning-bg,#fef3c7)] text-[var(--severity-warning-text,#92400e)]"
    : "bg-nav-active/10 text-nav-active";
  return (
    <span title={m.error || undefined}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {MEETING_ACTIVE.has(m.status) && <Loader2 size={10} className="animate-spin" />}
      {label}
    </span>
  );
}

function MeetingUpload({ onUploaded, orgs = [], orgLabel = (s) => s }) {
  const [file, setFile] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  // PRIVATE by default — sharing with an org is the explicit checkbox (plus
  // an org picker for multi-org members).
  const [share, setShare] = useState(false);
  const [shareOrg, setShareOrg] = useState("");
  const [pct, setPct] = useState(null); // null = idle, number = uploading
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) { toast.error("pick an audio/video file first"); return; }
    setPct(0);
    try {
      const meta = await fetchData("meetings", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name, content_type: file.type || "application/octet-stream",
          meeting_date: date, is_personal: !share, size: file.size,
          ...(share ? { org_slug: shareOrg || orgs[0] } : {}),
        }),
      });
      // Straight to S3 with a presigned PUT — bytes never hit the backend,
      // and XHR gives the honest upload percentage.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", meta.put_url);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setPct(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 PUT ${xhr.status}`)));
        xhr.onerror = () => reject(new Error("upload failed"));
        xhr.send(file);
      });
      await fetchData(`meetings/${meta.id}/complete`, { method: "POST" });
      toast.success("uploaded — transcription started");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onUploaded();
    } catch (e) {
      toast.error(String(e?.message || "upload failed"));
    } finally { setPct(null); }
  };

  return (
    <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Mic size={16} strokeWidth={2.5} className="text-nav-active" />
        <span className="text-sm font-semibold text-page-title">Upload a call recording</span>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {/* .mkv listed explicitly: OBS records Matroska and macOS does not
            always map it onto video/*, greying it out in the picker. */}
        <input ref={inputRef} type="file" accept="audio/*,video/*,.mkv,.webm"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm text-page-text file:mr-3 file:rounded-md file:border file:border-page-muted/50 file:bg-transparent file:text-page-text file:text-sm file:px-3 file:py-1.5 file:cursor-pointer" />
        <label className="inline-flex items-center gap-1.5 text-sm text-page-muted">
          date
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={selectCls} />
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm text-page-muted cursor-pointer"
          title="Uploads are private to you by default — check to make this one visible to your organization">
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)}
            className="accent-[var(--nav-active)]" />
          share with organization
        </label>
        {share && orgs.length > 1 && (
          <select value={shareOrg || orgs[0]} onChange={(e) => setShareOrg(e.target.value)} className={selectCls}>
            {orgs.map((o) => <option key={o} value={o}>{orgLabel(o)}</option>)}
          </select>
        )}
        <button type="button" className={`${btnPrimary} ml-auto`} disabled={pct !== null || !file} onClick={upload}>
          {pct !== null ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={2.5} />}
          {pct !== null ? `Uploading ${pct}%` : "Upload"}
        </button>
      </div>
      {pct !== null && (
        <div className="h-1.5 rounded-full bg-table-stripe overflow-hidden">
          <div className="h-full bg-nav-active transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="text-xs text-page-muted">
        Audio or video — an OBS/QuickTime screen recording works as is, the audio track is
        extracted automatically. Transcription runs on our cluster (Whisper), then the summary
        lands here. A 30-minute call takes ~20–40 minutes. Meetings are private to you unless
        you share them — here, or later with the Share button on the card.
      </p>
    </div>
  );
}

// ── Project specification status (spec-aware task drafting) ─────────────────
// Page-level caches keyed by project slug, shared by every card: ONE
// docs-engine probe per slug per visit, and ONE shared "generation queued"
// record per slug — nine action items assigned to the same project never file
// nine generation jobs. The refresh request body mirrors the Docs page's
// "Refresh spec" button verbatim, so both surfaces stay one mechanism.
const SPEC_REFRESH_REQUEST = {
  kind: "change_project",
  title: "Refresh the project specification",
  details: [
    "Regenerate/refresh this project's SPECIFICATION pages in the fork Knowledge Base (vault entries, type 'spec'):",
    "",
    "- `spec-overview` — what the project is and does, key decisions, change history (from the request briefs).",
    "- `spec-data-model` — sources, data elements, mappings, rules, targets.",
    "- `spec-pipelines` — the transformation graph, when it runs, what lands where.",
    "- `spec-workflows` — each business process, its actors and gates (if any).",
    "- `spec-app-<name>` — one page per published app; `spec-agent-<name>` per agent (if any).",
    "",
    "Derive content from the ACTUAL current state (project_status, sources, elements, workflows, apps, agents) plus the request history. Wikilink pages to each other and from the `project` note. Do not touch anything else.",
  ].join("\n"),
};

function useSpecStatus() {
  const [status, setStatus] = useState({}); // slug → 'loading' | 'ok' | 'none' | 'nofork'
  const [inFlight, setInFlight] = useState({}); // slug → request id of a queued generation
  const probed = useRef(new Set());

  // Seed the in-flight map once per visit from the open requests, exactly the
  // way the Docs page detects its yellow strip — a refresh queued from Docs
  // (or by a teammate) shows here too.
  useEffect(() => {
    fetchData("app_requests?status=open&all=1")
      .then((r) => {
        const jobs = {};
        for (const x of r?.requests || []) {
          if (["queued", "claimed", "running"].includes(x.status) && /specification/i.test(String(x.title || ""))) {
            jobs[x.project_slug] = x.id;
          }
        }
        setInFlight((prev) => ({ ...jobs, ...prev }));
      })
      .catch(() => {});
  }, []);

  const probe = useCallback(async (slug) => {
    const r = await fetchData("docs/engine", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Cortex-Project": slug, "X-Cortex-Branch": "main" },
      body: JSON.stringify({ action: "list", params: { type: "spec" } }),
    });
    return (Array.isArray(r?.data) ? r.data : []).length ? "ok" : "none";
  }, []);

  const ensure = useCallback((slug) => {
    if (!slug || probed.current.has(slug)) return;
    probed.current.add(slug);
    setStatus((s) => ({ ...s, [slug]: "loading" }));
    probe(slug)
      .then((st) => setStatus((s) => ({ ...s, [slug]: st })))
      .catch((e) => setStatus((s) => ({ ...s, [slug]: /fork/i.test(String(e?.message || "")) ? "nofork" : "none" })));
  }, [probe]);

  // While any generation is in flight, watch the open queue; when a job
  // leaves it (done or failed), re-probe that project so the chip flips to
  // ✓ (or back to the generate button) without a page reload.
  useEffect(() => {
    const slugs = Object.keys(inFlight);
    if (!slugs.length) return;
    const t = setInterval(async () => {
      let stillOpen;
      try {
        const r = await fetchData("app_requests?status=open&all=1");
        stillOpen = new Set((r?.requests || [])
          .filter((x) => ["queued", "claimed", "running"].includes(x.status) && /specification/i.test(String(x.title || "")))
          .map((x) => x.project_slug));
      } catch { return; }
      for (const slug of slugs) {
        if (stillOpen.has(slug)) continue;
        setInFlight((f) => { const n = { ...f }; delete n[slug]; return n; });
        try {
          const st = await probe(slug);
          setStatus((s) => ({ ...s, [slug]: st }));
          if (st === "ok") toast.success(`Specification for ${slug} is ready — task drafts now use it.`);
          else toast.error(`Specification generation for ${slug} did not complete — see the Board.`);
        } catch { /* keep the previous status */ }
      }
    }, 45_000);
    return () => clearInterval(t);
  }, [inFlight, probe]);

  const refresh = useCallback(async (slug) => {
    if (!slug) return;
    try {
      const res = await createAppRequest(SPEC_REFRESH_REQUEST, { projectSlug: slug });
      const id = (res?.request || res)?.id;
      setInFlight((f) => ({ ...f, [slug]: id || true }));
      toast.success(`Specification generation queued for ${slug}${id ? ` (#${id})` : ""} — takes ~10 min.`);
    } catch (e) {
      toast.error(String(e?.message || "could not queue the generation"));
    }
  }, []);

  return { status, inFlight, ensure, refresh };
}

// Tiny status line for a picked project: spec present / missing (+ the
// deduped Refresh affordance) / no fork. Renders nothing until a probe ran.
function SpecChip({ slug, spec }) {
  if (!slug || !spec) return null;
  const st = spec.status[slug];
  const job = spec.inFlight[slug];
  if (job && st !== "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-page-muted whitespace-nowrap">
        <Loader2 size={11} className="animate-spin" /> spec generation queued{typeof job === "number" ? ` (#${job})` : ""}
      </span>
    );
  }
  if (st === "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-page-muted whitespace-nowrap" title="Task drafts for this project use its specification">
        <CircleCheck size={11} strokeWidth={2.5} className="text-nav-active" /> spec
      </span>
    );
  }
  if (st === "nofork") {
    return <span className="text-[11px] text-page-muted whitespace-nowrap" title="This project has no paired Flow fork — a specification cannot be stored for it">no spec store</span>;
  }
  if (st === "none") {
    return (
      <button type="button"
        className="inline-flex items-center gap-1 text-[11px] text-page-muted underline underline-offset-2 bg-transparent border-0 cursor-pointer p-0 whitespace-nowrap"
        title="No specification yet — queue the agent to generate it from the project's actual state (~10 min); task drafts then use it"
        onClick={() => spec.refresh(slug)}>
        <RefreshCw size={11} strokeWidth={2.5} /> no spec — generate
      </button>
    );
  }
  return null;
}

function MeetingCard({ meeting: m, projects, onChanged, onDeleted, spec, me, orgs = [], orgLabel = (s) => s, isAdmin = false }) {
  // Owner-only: the ORIGINAL words (transcript_raw) behind the redacted view.
  const [rawDetail, setRawDetail] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [showDismissedTopics, setShowDismissedTopics] = useState(false);
  const [target, setTarget] = useState(m.project_slug || "");
  const [routing, setRouting] = useState(false);
  // Share/Unshare — the owner's per-meeting privacy switch. Private is the
  // default for every import and upload; Share picks ONE of the owner's orgs.
  const isOwner = !!me && m.uploaded_by === me;
  const [shareOrg, setShareOrg] = useState("");
  const [sharing, setSharing] = useState(false);
  const shareMeeting = async () => {
    const org = shareOrg || orgs[0];
    if (!org) { toast.error("you are not a member of any organization"); return; }
    setSharing(true);
    try {
      await fetchData(`meetings/${m.id}/share`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_slug: org }),
      });
      toast.success(`shared with ${orgLabel(org)} — every member can see this meeting now`);
      onChanged();
    } catch (e) {
      toast.error(String(e?.message || "could not share the meeting"));
    } finally { setSharing(false); }
  };
  const unshareMeeting = async () => {
    setSharing(true);
    try {
      await fetchData(`meetings/${m.id}/unshare`, { method: "POST" });
      toast.success("meeting is private again — only you see it");
      onChanged();
    } catch (e) {
      toast.error(String(e?.message || "could not unshare the meeting"));
    } finally { setSharing(false); }
  };
  const [detail, setDetail] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  // Probe spec status for every project already visible on this card, so the
  // chips render without a first click (one deduped probe per slug page-wide).
  useEffect(() => {
    if (!spec) return;
    const slugs = new Set();
    if (m.project_slug) slugs.add(m.project_slug);
    for (const t of Array.isArray(m.topics) ? m.topics : []) {
      if (t.project_slug) slugs.add(t.project_slug);
    }
    slugs.forEach((s) => spec.ensure(s));
  }, [m.project_slug, m.topics, spec]);

  const loadDetail = async () => {
    if (detail) { setShowTranscript((v) => !v); return; }
    try {
      const r = await fetchData(`meetings/${m.id}`);
      setDetail(r.meeting);
      setShowTranscript(true);
    } catch (e) { toast.error(String(e?.message || "could not load the transcript")); }
  };

  const route = async () => {
    if (!target) { toast.error("pick a project first"); return; }
    setRouting(true);
    try {
      await fetchData(`meetings/${m.id}/route`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_slug: target }),
      });
      toast.success(`sent to ${target} → Docs / Knowledge`);
      onChanged();
    } catch (e) {
      toast.error(String(e?.message || "routing failed"));
    } finally { setRouting(false); }
  };

  const remove = async () => {
    try {
      await fetchData(`meetings/${m.id}`, { method: "DELETE" });
      onDeleted(m.id);
    } catch (e) { toast.error(String(e?.message || "delete failed")); }
  };

  const summary = detail?.summary_md ?? null;
  return (
    <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-2" data-testid={`meeting-${m.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-page-title">{m.title || `Meeting ${String(m.meeting_date).slice(0, 10)}`}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px] text-page-muted">
            <span>{fmtDay(m.meeting_date)}</span>
            {m.duration_sec > 0 && <span>{Math.round(m.duration_sec / 60)} min</span>}
            <span className="inline-flex items-center gap-0.5 rounded bg-table-stripe px-1.5 py-0.5">
              {m.source || "upload"}
            </span>
            {m.is_personal ? (
              <span title="Only you can see this meeting"
                className="inline-flex items-center gap-0.5 rounded bg-table-stripe px-1.5 py-0.5"><Lock size={10} strokeWidth={2.5} /> private</span>
            ) : (
              <span title={`Visible to every member of ${orgLabel(m.org_slug)}`}
                className="inline-flex items-center gap-0.5 rounded bg-nav-active/10 text-nav-active px-1.5 py-0.5">
                <Users size={10} strokeWidth={2.5} /> {orgLabel(m.org_slug)}
              </span>
            )}
            <span>by {m.uploaded_by}</span>
            <MeetingStatusChip m={m} />
          </div>
        </div>
        {/* Owner-only privacy switch: Share (with an org picker for multi-org
            members) / Unshare. Sits left of routed-chip/delete. */}
        {isOwner && (m.is_personal ? (
          <div className="flex items-center gap-1.5 shrink-0">
            {orgs.length > 1 && (
              <select value={shareOrg || orgs[0]} onChange={(e) => setShareOrg(e.target.value)}
                className={selectCls + " text-xs py-0.5"} data-testid={`meeting-share-org-${m.id}`}>
                {orgs.map((o) => <option key={o} value={o}>{orgLabel(o)}</option>)}
              </select>
            )}
            <button type="button" className={btnGhost} disabled={sharing} onClick={shareMeeting}
              title="Make this meeting visible to your organization" data-testid={`meeting-share-${m.id}`}>
              {sharing ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} strokeWidth={2.5} />} Share
            </button>
          </div>
        ) : (
          <button type="button" className={`${btnGhost} shrink-0`} disabled={sharing} onClick={unshareMeeting}
            title={`Shared with ${orgLabel(m.org_slug)} — make it private again`} data-testid={`meeting-unshare-${m.id}`}>
            {sharing ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} strokeWidth={2.5} />} Unshare
          </button>
        ))}
        {summaryFailed(m) && (isOwner || isAdmin) && (
          <button type="button" className={`${btnGhost} shrink-0`} title={`Run the summary again — ${m.error}`}
            onClick={async () => {
              try { await fetchData(`meetings/${m.id}/resummarize`, { method: "POST" }); toast.success("summary queued again"); onChanged(); }
              catch (e) { toast.error(String(e?.message || "could not queue the summary")); }
            }} data-testid={`meeting-resummarize-${m.id}`}>
            <RefreshCw size={13} strokeWidth={2.5} /> Retry
          </button>
        )}
        {m.routed_at ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-nav-active/10 text-nav-active px-2 py-0.5 text-[11px] font-medium shrink-0">
            <CircleCheck size={11} strokeWidth={2.5} /> {m.project_slug}
          </span>
        ) : (
          <button type="button" title="Delete" onClick={remove}
            className="p-1 rounded-md border-0 bg-transparent text-page-muted cursor-pointer shrink-0">
            <Trash2 size={15} strokeWidth={2.5} />
          </button>
        )}
      </div>
      {m.status === "done" && (
        <MeetingSummary id={m.id} detail={detail} setDetail={setDetail} />
      )}
      {/* TOPICS — the subjects the call covered, each routable on its own. */}
      {m.status === "done" && Array.isArray(m.topics) && m.topics.length > 0 && (() => {
        const all = m.topics;
        const active = all.filter((t) => t.status !== "dismissed");
        const hidden = all.length - active.length;
        const shown = showDismissedTopics ? all : active;
        return (
          <div className="pt-2 border-t border-page-divider space-y-1.5" data-testid={`meeting-topics-${m.id}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">
              Topics ({active.length})
              {hidden > 0 && (
                <button type="button"
                  className="ml-2 normal-case font-normal tracking-normal underline underline-offset-2 bg-transparent border-0 cursor-pointer p-0 text-page-muted"
                  onClick={() => setShowDismissedTopics((v) => !v)}>
                  {showDismissedTopics ? "hide dismissed" : `${hidden} dismissed`}
                </button>
              )}
            </div>
            {shown.map((t) => (
              <MeetingTopicRow key={t.id} meetingId={m.id} topic={t} projects={projects} spec={spec}
                canManage={isOwner || isAdmin} onChanged={onChanged} />
            ))}
          </div>
        );
      })()}
      {showTranscript && (detail?.transcript || detail?.has_raw) && (
        <div className="space-y-1.5">
          {showRaw && rawDetail?.transcript_raw ? (
            <>
              <div className="inline-flex items-center gap-1 text-[11px] text-page-muted"><Lock size={11} strokeWidth={2.5} /> original transcript — only you can see this</div>
              <pre className="text-xs text-page-text bg-table-stripe rounded-md p-3 max-h-72 overflow-y-auto whitespace-pre-wrap border border-dashed border-card-border">{rawDetail.transcript_raw}</pre>
            </>
          ) : (
            <TranscriptView text={detail?.transcript || ""} />
          )}
          {detail?.has_raw && (
            <button type="button" className="text-[11px] text-page-muted underline underline-offset-2 bg-transparent border-0 cursor-pointer p-0"
              onClick={async () => {
                if (showRaw) { setShowRaw(false); return; }
                if (!rawDetail) {
                  try { const r = await fetchData(`meetings/${m.id}?raw=1`); setRawDetail(r.meeting); }
                  catch (e) { toast.error(String(e?.message || "could not load the original")); return; }
                }
                setShowRaw(true);
              }}
              data-testid={`meeting-raw-toggle-${m.id}`}>
              {showRaw ? "show what others see" : "show original (only you)"}
            </button>
          )}
        </div>
      )}
      {m.status === "done" && (
        <div className="flex items-center gap-2 pt-1 border-t border-page-divider">
          <button type="button" className="text-xs text-page-muted underline underline-offset-2 bg-transparent border-0 cursor-pointer p-0"
            onClick={loadDetail}>
            {showTranscript ? "hide transcript" : "show transcript"}
          </button>
          {/* Route (→ Docs) sends the WHOLE summary as a Knowledge page; the
              Board is reached only through topics → requirements. */}
          <div className="ml-auto flex items-center gap-2">
            {!m.routed_at && (
              <>
                {/* Distinct placeholder from the per-item selects above: this
                    one sends the SUMMARY to a project's Docs, not a task. */}
                <select value={target} onChange={(e) => setTarget(e.target.value)} className={selectCls + " max-w-52 truncate"}>
                  <option value="">route summary to…</option>
                  {projects.map((p) => <option key={p.slug} value={p.slug}>{p.name || p.slug}</option>)}
                </select>
                <button type="button" className={btnGhost} disabled={routing || !target} onClick={route}
                  title="Send this meeting's summary to the project's Docs → Knowledge — the project team will see the summary (the meeting itself stays as is)">
                  {routing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />} Route
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Summary body loads lazily (the list endpoint sends metadata only).
function MeetingSummary({ id, detail, setDetail }) {
  useEffect(() => {
    if (detail) return;
    let alive = true;
    fetchData(`meetings/${id}`).then((r) => { if (alive) setDetail(r.meeting); }).catch(() => {});
    return () => { alive = false; };
  }, [id, detail, setDetail]);
  if (!detail) return <div className="text-xs text-page-muted"><Loader2 size={12} className="animate-spin inline mr-1" />loading summary…</div>;
  return (
    <div className="max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{detail.summary_md || ""}</ReactMarkdown>
    </div>
  );
}

// Rank for the Status column sort: the more alive, the higher.
const channelStatusRank = (ch) =>
  !ch.present ? 0 : !ch.is_member ? 1 : !ch.enabled ? 2 : ch.catching_up ? 3 : 4;

const CHANNEL_SORTERS = {
  name: (a, b) => String(a.name).localeCompare(String(b.name)),
  members: (a, b) => (a.num_members ?? -1) - (b.num_members ?? -1),
  activity: (a, b) => (Number(a.last_activity) || 0) - (Number(b.last_activity) || 0),
  status: (a, b) => channelStatusRank(a) - channelStatusRank(b),
  listen: (a, b) => Number(!!a.enabled) - Number(!!b.enabled),
};

const channelMatches = (ch, filter) => {
  const active = Number(ch.last_activity) > Date.now() / 1000 - ACTIVE_WINDOW_SEC;
  switch (filter) {
    case "listening": return !!ch.enabled;
    case "active": return ch.present && active;
    case "quiet": return ch.present && !active;
    case "gone": return !ch.present;
    default: return true;
  }
};

const fmtDay = (day) => {
  const key = String(day).slice(0, 10);
  // Day keys are UTC dates (that's how the digester buckets messages), so
  // compare against UTC today/yesterday too.
  const todayKey = new Date().toISOString().slice(0, 10);
  const yesterdayKey = new Date(Date.now() - 24 * 3600 * 1000).toISOString().slice(0, 10);
  if (key === todayKey) return "Today";
  if (key === yesterdayKey) return "Yesterday";
  const d = new Date(day);
  return Number.isNaN(d.getTime()) ? String(day) : d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
};

function StatusPill({ ch }) {
  let label = "off", cls = "bg-table-stripe text-page-muted";
  let title;
  if (!ch.present) { label = "gone"; }
  else if (!ch.is_member) { label = "not a member"; }
  else if (ch.enabled && ch.catching_up) {
    // cursor_ts = how far the backfill has walked so far; the next collect
    // run continues from there (20 API pages per channel per run).
    label = `catching up · till ${fmtActivity(ch.cursor_ts)}`;
    title = `History up to ${fmtActivity(ch.cursor_ts)} is collected; the next run continues from that date towards today.`;
    cls = "bg-nav-active/10 text-nav-active";
  }
  else if (ch.enabled) { label = "listening"; cls = "bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)]"; }
  return <span title={title} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${cls}`}>{label}</span>;
}

function TopicCard({ topic, projects, onDismissed, onChanged, spec }) {
  const [busy, setBusy] = useState(null); // 'route' | project slug (remove) | 'dismiss'
  // Every project the topic is in (feed_route); pre-feed_route rows only know
  // their single project_slug.
  const routes = (topic.routes && topic.routes.length)
    ? topic.routes
    : (topic.status === "routed" && topic.project_slug ? [{ project_slug: topic.project_slug }] : []);
  const sent = routes.length > 0;
  const suggested = topic.project_slug && !routes.some((r) => r.project_slug === topic.project_slug) ? topic.project_slug : null;
  const pct = topic.confidence != null ? Math.round(topic.confidence * 100) : null;
  const projectName = (slug) => projects.find((p) => p.slug === slug)?.name || slug;
  // Preselect the best-matching project; nothing when the digest matched none.
  const [target, setTarget] = useState(suggested || "");
  const post = async (path, body) => fetchData(`slack/topics/${topic.id}/${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
  });
  const send = async () => {
    if (!target) { toast.error("pick a project first"); return; }
    setBusy("route");
    try {
      await post("route", { project_slug: target });
      toast.success(`"${topic.title}" is now in ${projectName(target)}: Knowledge page + requirement source added, requirements review scheduled`);
      setTarget("");
      onChanged?.();
    } catch (e) { toast.error(String(e?.message || "could not send")); }
    finally { setBusy(null); }
  };
  const remove = async (slug) => {
    setBusy(slug);
    try {
      await post("unroute", { project_slug: slug });
      toast.success(`removed from ${projectName(slug)}`);
      onChanged?.();
    } catch (e) { toast.error(String(e?.message || "could not remove")); }
    finally { setBusy(null); }
  };
  const dismiss = async () => {
    setBusy("dismiss");
    try { await post("dismiss"); onDismissed(topic.id); }
    catch (e) { toast.error(String(e?.message || "dismiss failed")); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-2" data-testid={`feed-topic-${topic.id}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-page-title">{topic.title}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px] text-page-muted">
            {(topic.channels || []).map((c) => (
              <span key={c} className="inline-flex items-center gap-0.5 rounded bg-table-stripe px-1.5 py-0.5"><Hash size={10} strokeWidth={2.5} />{c}</span>
            ))}
            <span className="inline-flex items-center gap-1"><MessagesSquare size={11} strokeWidth={2.5} />{topic.msg_count}</span>
            <span>{fmtTs(topic.first_ts)} – {fmtTs(topic.last_ts)}</span>
            {topic.is_personal && (
              <span className="inline-flex items-center gap-0.5 rounded bg-table-stripe px-1.5 py-0.5" title="From your personal chats — only you see this topic">
                <Lock size={10} strokeWidth={2.5} /> personal
              </span>
            )}
          </div>
        </div>
        {/* Where it is — the same two states as a meeting topic. */}
        <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
          {sent ? (
            <RouteChips routes={routes} canManage onRemove={remove} busy={busy} />
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-medium"
              title={topic.route_error ? `Automatic send failed: ${topic.route_error}` : "This topic has not been sent to any project"}>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
              Not in any project yet
              {suggested && <span className="font-normal opacity-90">· looks like {projectName(suggested)}{pct != null ? ` (${pct}%)` : ""}</span>}
              {topic.route_error && <span className="font-normal">· automatic send failed</span>}
            </span>
          )}
        </div>
      </div>
      <div className="max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{topic.summary_md || ""}</ReactMarkdown>
      </div>
      {/* Actions: ONE select (best match preselected) + ONE Send; the Board is
          reached only through the project's requirements. */}
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-page-divider">
        <select value={target} onChange={(e) => { setTarget(e.target.value); if (e.target.value) spec?.ensure(e.target.value); }}
          className={selectCls + " text-xs h-8 w-56 truncate"} data-testid={`feed-topic-project-${topic.id}`}>
          <option value="">{sent ? "another project…" : "choose a project…"}</option>
          {projects.filter((p) => !routes.some((r) => r.project_slug === p.slug))
            .map((p) => <option key={p.slug} value={p.slug}>{p.name || p.slug}</option>)}
        </select>
        <button type="button" className={btnGhost + " shrink-0 h-8"} disabled={busy === "route" || !target} onClick={send}
          title={target ? `Send this topic to ${projectName(target)}: Docs → Knowledge page + Requirement source (summary + the redacted messages)` : "Choose a project first"}
          data-testid={`feed-topic-send-${topic.id}`}>
          {busy === "route" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2.5} />} Send
        </button>
        <SpecChip slug={target} spec={spec} />
        {!sent && (
          <button type="button" disabled={busy === "dismiss"} onClick={dismiss}
            className="ml-auto border-0 bg-transparent p-0 cursor-pointer text-[11px] text-page-muted underline underline-offset-2 hover:text-page-text"
            title="Hide this topic — it is not worth keeping" data-testid={`feed-topic-dismiss-${topic.id}`}>
            not a topic
          </button>
        )}
      </div>
    </div>
  );
}

export default function Feed() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Spec-aware task drafting: one shared status/refresh cache for the page.
  const spec = useSpecStatus();
  const tabParam = searchParams.get("tab");
  const tab = ["channels", "mychats", "settings", "meetings"].includes(tabParam) ? tabParam : "digests";
  const statusFilter = searchParams.get("status") || "";
  // The Feed is a merged view across every org the caller is in; this narrows
  // Digests + Meetings to one org (shown only when the user has several).
  const orgFilter = searchParams.get("org") || "";
  // Settings sub-screens live in the URL too: st = slack | meetings picks the
  // sub-tab, service = the opened meeting-service page. Reload / share lands
  // on the same screen.
  const stTab = ["meetings", "privacy"].includes(searchParams.get("st")) ? searchParams.get("st") : "slack";
  const svcParam = searchParams.get("service");
  const svc = ["fireflies", "krisp"].includes(svcParam) ? svcParam : null;
  const { user } = useAuth();
  const isAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
  const myEmail = String(user?.email || "").toLowerCase();

  const [status, setStatus] = useState(null);
  const [topics, setTopics] = useState(null);
  const [channels, setChannels] = useState(null);
  // Orgs whose group channels the caller manages (null = all, e.g. platform admin).
  const [manageableOrgs, setManageableOrgs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [collecting, setCollecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [modeDraft, setModeDraft] = useState({}); // channel_id -> backfill_mode before enabling
  const [meetings, setMeetings] = useState(null);
  // Org workspace token(s) — null means "not an org admin" (403 from the API).
  const [orgTokens, setOrgTokens] = useState(null);
  const [orgTokenInput, setOrgTokenInput] = useState("");
  const [orgTokenOrg, setOrgTokenOrg] = useState("");
  const [savingOrgToken, setSavingOrgToken] = useState(false);
  // Meeting-service integrations (fireflies, krisp, …) — the caller's OWN
  // personal connections (member-level); null means the fetch failed.
  const [integ, setInteg] = useState(null);
  const [ffKeyInput, setFfKeyInput] = useState("");
  const [savingFf, setSavingFf] = useState(false);
  const [savingKrisp, setSavingKrisp] = useState(false);
  // Krisp's second, independent connection: the Meeting Assistant API key
  // (pull + history backfill), coexisting with the webhook.
  const [kaKeyInput, setKaKeyInput] = useState("");
  const [savingKa, setSavingKa] = useState(false);
  // The Meetings tab's one-click "check every connected service" sync.
  const [mtSyncing, setMtSyncing] = useState(false);
  // Personal token + own chats (Settings / My chats tabs).
  // One personal-token connection per org (the same person may bring their
  // chats to several orgs with independent listen/share toggles).
  const [myTokens, setMyTokens] = useState([]);
  const [myMemberOrgs, setMyMemberOrgs] = useState(null);
  // slug → display name (from the organization table); slugs remain the values
  // everywhere (options, URL, API bodies) — only what the eye sees is renamed.
  const [orgNames, setOrgNames] = useState({});
  const orgLabel = (slug) => orgNames[slug] || slug;
  // Orgs the caller can share meetings into: the fresh member_orgs list from
  // the slack-token GET once it has loaded, else the login-time session orgs.
  const meetingOrgs = useMemo(() => {
    if (Array.isArray(myMemberOrgs) && myMemberOrgs.length) return myMemberOrgs;
    return [...new Set((user?.orgs || [])
      .filter((o) => ["platform_admin", "org_admin", "member"].includes(o.role))
      .map((o) => o.org))];
  }, [myMemberOrgs, user]);
  const [tokenOrg, setTokenOrg] = useState("");
  const [myChats, setMyChats] = useState([]);
  const [tokenInput, setTokenInput] = useState("");
  const [tokenMode, setTokenMode] = useState("both");
  const [savingToken, setSavingToken] = useState(false);
  const [chFilter, setChFilter] = useState("all");
  // Digests tab: multi-select channel filter (empty set = all channels) and a
  // source-kind filter (group channels vs personal chats).
  const [chanFilter, setChanFilter] = useState(() => new Set());
  const [srcFilter, setSrcFilter] = useState("all"); // all | groups | personal

  const toggleChanFilter = (name) =>
    setChanFilter((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  // Default: most recently active on top — that's the "what should I listen
  // to" view. null = backend order (enabled first, then name).
  const [chSort, setChSort] = useState({ field: "activity", dir: "desc" });

  const toggleChSort = (field) =>
    setChSort((prev) => prev?.field === field
      ? (prev.dir === "asc" ? { field, dir: "desc" } : null)
      : { field, dir: "asc" });

  const chSortIcon = (field) => {
    if (chSort?.field !== field) return <ArrowUpDown size={11} className="text-page-muted/60" />;
    return chSort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />;
  };

  const setParams = useCallback((next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) { if (v == null) p.delete(k); else p.set(k, v); }
      return p;
    });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    try {
      const [st, tp, ch, tok, mine, mt] = await Promise.all([
        fetchData("slack/status"),
        fetchData(`slack/topics?${new URLSearchParams({
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(orgFilter ? { org: orgFilter } : {}),
        })}`),
        fetchData("slack/channels"),
        fetchData("slack/token").catch(() => ({ tokens: [] })),
        fetchData("slack/my_chats").catch(() => ({ chats: [] })),
        fetchData(`meetings${orgFilter ? `?org=${encodeURIComponent(orgFilter)}` : ""}`).catch(() => ({ meetings: [] })),
      ]);
      setStatus(st);
      setTopics(tp.topics || []);
      setChannels(ch.channels || []);
      setManageableOrgs(ch.manageable_orgs ?? null);
      setMyTokens(tok.tokens || []);
      setMyMemberOrgs(tok.member_orgs ?? null);
      setOrgNames(tok.org_names || {});
      setMyChats(mine.chats || []);
      setMeetings(mt.meetings || []);
      // Separate: 403 for non-admins is expected, not an error.
      fetchData("slack/org_token")
        .then((r) => setOrgTokens({ tokens: r.org_tokens || [], adminOrgs: r.admin_orgs }))
        .catch(() => setOrgTokens(null));
      fetchData("integrations")
        .then((r) => setInteg({ integrations: r.integrations || [] }))
        .catch(() => setInteg(null));
    } catch (e) {
      toast.error(String(e?.message || "could not load the feed"));
      setTopics([]); setChannels([]);
    }
  }, [statusFilter, orgFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listProjects().then(setProjects).catch(() => {}); }, []);

  // While any meeting is being processed — or its parallel action-items
  // extraction is still pending — poll its progress every 5s. A row flipping
  // into 'discarded' mid-watch is the uploader's own upload the summary judged
  // empty (the backend hides everyone else's) — surface it as a toast, since
  // the row will never grow a summary.
  useEffect(() => {
    if (!(meetings || []).some((m) => isMeetingActive(m))) return;
    const t = setTimeout(async () => {
      try {
        const r = await fetchData(`meetings${orgFilter ? `?org=${encodeURIComponent(orgFilter)}` : ""}`);
        const prev = new Map((meetings || []).map((m) => [m.id, m.status]));
        for (const m of r.meetings || []) {
          if (m.status === "discarded" && prev.has(m.id) && prev.get(m.id) !== "discarded") {
            toast(`"${m.title || `Meeting ${String(m.meeting_date).slice(0, 10)}`}" had no useful content — the meeting was not added`);
          }
        }
        setMeetings(r.meetings || []);
      } catch { /* next tick retries */ }
    }, 5000);
    return () => clearTimeout(t);
  }, [meetings, orgFilter]);

  const refresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  // Watch a server-side run: the truth about "is a collection running" lives
  // in /api/slack/status (`collecting` = pg-boss queue state), NOT in local
  // memory — so a page reload picks a run back up (see the useEffect below).
  // While watching, channels reload every tick so the catching-up cursor
  // ("till <date>") visibly advances.
  const watchingRef = useRef(false);
  const watchRun = useCallback(async () => {
    if (watchingRef.current) return;
    watchingRef.current = true;
    setCollecting(true);
    try {
      const deadline = Date.now() + 15 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        let st;
        try {
          st = await fetchData("slack/status");
          const ch = await fetchData("slack/channels");
          setChannels(ch.channels || []);
          const mine = await fetchData("slack/my_chats").catch(() => null);
          if (mine) setMyChats(mine.chats || []);
        } catch { continue; }
        setStatus(st);
        if (!st.collecting) {
          await load();
          toast.success(`collection finished — ${st.state?.last_message_count ?? 0} new messages, feed updated`);
          return;
        }
      }
      toast("collection is taking a while — it keeps running server-side, Refresh later");
    } finally {
      watchingRef.current = false;
      setCollecting(false);
    }
  }, [load]);

  const collectNow = async () => {
    try {
      await fetchData("slack/collect", { method: "POST" });
      toast.success("collection started");
      watchRun();
    } catch (e) {
      toast.error(String(e?.message || "could not queue collection"));
    }
  };

  // A run queued by cron, a colleague, or a pre-reload click: resume tracking.
  useEffect(() => {
    if (status?.collecting) watchRun();
  }, [status?.collecting, watchRun]);

  // Meetings tab sync: one click asks every connected meeting service for new
  // calls AND pulls the result into the list — no separate Refresh needed.
  const syncMeetings = async () => {
    setMtSyncing(true);
    try {
      const r = await fetchData("meetings/sync", { method: "POST" });
      if (!(r.queued || []).length) {
        toast("you have no meeting services connected — add your Fireflies or Krisp key in Settings");
        return;
      }
      const deadline = Date.now() + 5 * 60_000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 3000));
        const st = await fetchData("meetings/sync_status").catch(() => null);
        if (st && !st.syncing) {
          await load();
          const failed = (st.integrations || []).filter((i) => i.last_error);
          if (failed.length) {
            toast.error(`sync finished with errors: ${failed.map((i) => i.kind).join(", ")} — see Settings`);
          } else {
            toast.success("meetings synced");
          }
          return;
        }
      }
      toast("sync is taking a while — it keeps running server-side, check back later");
    } catch (e) {
      toast.error(String(e?.message || "could not start the sync"));
    } finally {
      setMtSyncing(false);
    }
  };

  const toggleChannel = async (ch, enabled) => {
    const mode = modeDraft[ch.channel_id] || ch.backfill_mode || "backfill";
    try {
      await fetchData(`slack/channels/${ch.channel_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        // org_slug disambiguates: the same channel may be a separate row per org.
        body: JSON.stringify({ enabled, backfill_mode: mode, org_slug: ch.org_slug }),
      });
      setChannels((prev) => prev.map((c) => c.channel_id === ch.channel_id && c.org_slug === ch.org_slug
        ? { ...c, enabled, backfill_mode: mode, catching_up: enabled && mode === "backfill" }
        : c));
    } catch (e) {
      toast.error(String(e?.message || "toggle failed"));
    }
  };

  // My chats: same PATCH endpoint, mirrored into the myChats slice.
  const patchMyChat = async (ch, patch) => {
    try {
      await fetchData(`slack/channels/${ch.channel_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...patch, org_slug: ch.org_slug }),
      });
      setMyChats((prev) => prev.map((c) => c.channel_id === ch.channel_id
        ? { ...c, ...patch, catching_up: (patch.enabled ?? c.enabled) && (patch.backfill_mode ?? c.backfill_mode) === "backfill" }
        : c));
      if ("shared" in patch) {
        toast.success(patch.shared ? "chat is now visible to everyone" : "chat is private again");
        load(); // shared chats appear/disappear on the Channels tab + topics rescope
      }
    } catch (e) {
      toast.error(String(e?.message || "update failed"));
    }
  };

  const saveToken = async (orgSlug) => {
    if (!tokenInput.trim()) { toast.error("paste the xoxp- token first"); return; }
    setSavingToken(true);
    try {
      const res = await fetchData("slack/token", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: tokenInput.trim(), mode: tokenMode, org_slug: orgSlug }),
      });
      toast.success(`connected as ${res.user_name} (${res.team}) to ${res.org_slug} — ${res.dms} chats, ${res.groups} group channels found`);
      setTokenInput("");
      await load();
    } catch (e) {
      toast.error(String(e?.message || "could not save the token"));
    } finally { setSavingToken(false); }
  };

  const removeToken = async (orgSlug) => {
    try {
      await fetchData("slack/token", {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_slug: orgSlug }),
      });
      toast.success(`token removed from ${orgSlug} — your chats there stopped collecting`);
      await load();
    } catch (e) {
      toast.error(String(e?.message || "could not remove the token"));
    }
  };

  const visibleChannels = useMemo(() => {
    const list = (channels || []).filter((ch) => channelMatches(ch, chFilter));
    if (!chSort) return list;
    const cmp = CHANNEL_SORTERS[chSort.field];
    if (!cmp) return list;
    const sorted = [...list].sort(cmp);
    return chSort.dir === "desc" ? sorted.reverse() : sorted;
  }, [channels, chFilter, chSort]);

  // Names of personal chats the caller can know about: their own chats plus
  // chats other people shared. Used to tell "personal chat" topics from
  // group-channel topics in the source-kind filter.
  const personalChatNames = useMemo(() => {
    const s = new Set(myChats.map((c) => c.name));
    for (const c of channels || []) if (c.owner_email) s.add(c.name);
    return s;
  }, [myChats, channels]);

  // All channel names that actually appear in loaded topics — the filter's
  // option list (channels without digests would only add noise). Follows the
  // source-kind filter: "personal chats" offers only personal chats, etc.
  const topicChannels = useMemo(() => {
    const names = [...new Set((topics || []).flatMap((t) => t.channels || []))].sort();
    if (srcFilter === "personal") return names.filter((n) => personalChatNames.has(n));
    if (srcFilter === "groups") return names.filter((n) => !personalChatNames.has(n));
    return names;
  }, [topics, srcFilter, personalChatNames]);

  // Switching the source kind may strand checked channels of the other kind —
  // silently keeping them would over-filter, so prune the selection.
  useEffect(() => {
    setChanFilter((prev) => {
      const allowed = new Set(topicChannels);
      const next = [...prev].filter((n) => allowed.has(n));
      return next.length === prev.size ? prev : new Set(next);
    });
  }, [topicChannels]);

  const topicsByDay = useMemo(() => {
    const m = new Map();
    for (const t of topics || []) {
      if (chanFilter.size > 0 && !(t.channels || []).some((c) => chanFilter.has(c))) continue;
      if (srcFilter !== "all") {
        const fromPersonal = t.is_personal || (t.channels || []).some((c) => personalChatNames.has(c));
        const fromGroups = (t.channels || []).some((c) => !personalChatNames.has(c));
        if (srcFilter === "personal" && !fromPersonal) continue;
        if (srcFilter === "groups" && !fromGroups) continue;
      }
      const key = String(t.day).slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(t);
    }
    return [...m.entries()];
  }, [topics, chanFilter, srcFilter, personalChatNames]);

  const RailBtn = ({ id, label, count, icon: Icon }) => (
    <button type="button" data-testid={`feed-tab-${id}`}
      onClick={() => setParams({ tab: id === "digests" ? null : id })}
      className={"w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium border-0 cursor-pointer text-left " +
        (tab === id ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/20")}>
      <span className="flex items-center gap-2">
        {Icon && <Icon size={15} strokeWidth={2.5} className="shrink-0" />}
        {label}
      </span>
      {count != null && <span className="text-[11px] text-page-muted">{count}</span>}
    </button>
  );

  return (
    <div className="flex h-full min-h-0 bg-page-bg" data-testid="feed-page">
      {/* Left rail: Sync/Refresh on top, tabs in the middle, Settings pinned
          at the bottom. Content scrolls in its own pane on the right. */}
      <aside className="w-60 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0 p-3">
        <div className="flex items-center gap-2 px-1 mb-3">
          <Rss size={20} className="text-nav-active" />
          <span className="text-lg font-medium text-page-title">Feed</span>
          {topics === null && <Loader2 size={14} className="animate-spin text-page-muted ml-auto" />}
        </div>
        <div className="flex items-center gap-2">
          {(isAdmin || orgTokens !== null) ? (
            <button type="button" className={`${btnPrimary} flex-1 justify-center`} disabled={collecting} onClick={collectNow}>
              {collecting ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} strokeWidth={2.5} />}
              {collecting ? "Syncing…" : "Sync Slack"}
            </button>
          ) : (
            <span className="flex-1 text-xs text-page-muted px-1">
              {collecting ? "syncing…" : "hourly auto-sync"}
            </span>
          )}
          <button type="button" title="Refresh all tabs" className={`${btnGhost} px-2`} disabled={refreshing} onClick={refresh}>
            {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} strokeWidth={2.5} />}
          </button>
        </div>
        {status && (
          <p className="text-[11px] text-page-muted px-1 mt-2 leading-relaxed">
            {status.state?.last_run ? `Last sync ${new Date(status.state.last_run).toLocaleString()}` : "No syncs yet"}
            <br />{status.messages ?? 0} messages · {status.topics ?? 0} topics
          </p>
        )}
        {status && status.configured === false && (
          <p className="text-[11px] text-page-muted px-1 mt-1">Org Slack token is not configured.</p>
        )}
        <nav className="flex-1 min-h-0 flex flex-col gap-1 mt-4">
          {/* Org filter lives on the rail: it scopes the loads behind the
              Digests/Meetings counts below, not just one tab's list. */}
          {(myMemberOrgs || []).length > 1 && (
            <select value={orgFilter} onChange={(e) => setParams({ org: e.target.value || null })}
              className={`${selectCls} w-full mb-2`} data-testid="feed-org-filter">
              <option value="">all organizations</option>
              {(myMemberOrgs || []).map((o) => <option key={o} value={o}>{orgLabel(o)}</option>)}
            </select>
          )}
          <RailBtn id="digests" label="Digests" count={topics?.length} />
          <RailBtn id="meetings" label="Meetings" count={meetings?.length} />
          <RailBtn id="channels" label="Channels"
            count={channels ? `${channels.filter((c) => c.enabled).length}/${channels.length}` : null} />
          {(myTokens.length > 0 || myChats.length > 0) && (
            <RailBtn id="mychats" label="My chats"
              count={myChats.length ? `${myChats.filter((c) => c.enabled).length}/${myChats.length}` : null} />
          )}
        </nav>
        <RailBtn id="settings" label="Settings" icon={SettingsIcon} />
      </aside>

      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
      <div className="px-6 py-5">
      {tab === "digests" && (
        <div className="space-y-6 max-w-4xl">
          <div className="flex items-center gap-2 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className={`${selectCls} inline-flex items-center gap-1.5`}>
                  <Hash size={14} strokeWidth={2.5} />
                  {chanFilter.size === 0 ? "all channels" : `${chanFilter.size} channel${chanFilter.size > 1 ? "s" : ""}`}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-card-bg border-card-border">
                <DropdownMenuItem onSelect={() => setChanFilter(new Set())} className="text-page-text">
                  All channels
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {topicChannels.map((name) => (
                  <DropdownMenuCheckboxItem key={name} checked={chanFilter.has(name)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => toggleChanFilter(name)} className="text-page-text">
                    #{name}
                  </DropdownMenuCheckboxItem>
                ))}
                {topicChannels.length === 0 && (
                  <DropdownMenuItem disabled className="text-page-muted">no digests yet</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <select value={srcFilter} onChange={(e) => setSrcFilter(e.target.value)} className={selectCls}>
              <option value="all">all sources</option>
              <option value="groups">group channels</option>
              <option value="personal">personal chats</option>
            </select>
            <select value={statusFilter} onChange={(e) => setParams({ status: e.target.value || null })} className={selectCls}>
              <option value="">all topics</option>
              <option value="cortex">cortex-wide</option>
              <option value="suggested">suggested</option>
              <option value="routed">routed</option>
            </select>
          </div>
          {topics !== null && topics.length === 0 && (
            <div className="text-sm text-page-muted py-8 text-center">
              No digests yet. Enable channels on the Channels tab, then Sync Slack — topics appear after the next digest pass.
            </div>
          )}
          {topics !== null && topics.length > 0 && topicsByDay.length === 0 && (
            <div className="text-sm text-page-muted py-8 text-center">
              No digests match the current filters.
            </div>
          )}
          {topicsByDay.map(([day, dayTopics]) => (
            <div key={day} className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-page-muted">{fmtDay(day)}</div>
              {dayTopics.map((t) => (
                <TopicCard key={t.id} topic={t} projects={projects} spec={spec}
                  onDismissed={(id) => setTopics((prev) => prev.filter((x) => x.id !== id))}
                  onChanged={load} />
              ))}
            </div>
          ))}
        </div>
      )}

      {tab === "channels" && (
        <div className="space-y-3">
        <div className="flex items-center">
          <select value={chFilter} onChange={(e) => setChFilter(e.target.value)} className={selectCls}>
            {CHANNEL_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </div>
        <div className="rounded-lg border border-card-border bg-card-bg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-table-header">
              <tr className="text-left text-table-header-text">
                {[["name", "Channel"], ["members", "Members"], ["activity", "Last activity"], ["status", "Status"]].map(([field, label]) => (
                  <th key={field} className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => toggleChSort(field)}
                      className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-medium text-table-header-text cursor-pointer hover:text-nav-active">
                      {label} {chSortIcon(field)}
                    </button>
                  </th>
                ))}
                <th className="px-3 py-2 font-medium">On enable</th>
                <th className="px-3 py-2 font-medium text-right">
                  <button type="button" onClick={() => toggleChSort("listen")}
                    className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-medium text-table-header-text cursor-pointer hover:text-nav-active">
                    Listen {chSortIcon("listen")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleChannels.map((ch, i) => (
                <tr key={`${ch.org_slug || ""}:${ch.channel_id}`} className={i % 2 ? "bg-table-stripe" : ""}>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 text-page-text">
                      {ch.owner_email ? <MessageCircle size={13} strokeWidth={2.5} className="text-page-muted" />
                        : ch.ch_type === "group" ? <Lock size={13} strokeWidth={2.5} className="text-page-muted" />
                        : <Hash size={13} strokeWidth={2.5} className="text-page-muted" />}
                      {ch.name}
                      {new Set((channels || []).map((c) => c.org_slug)).size > 1 && ch.org_slug && (
                        <span className="text-[10px] rounded bg-table-stripe px-1.5 py-0.5 text-page-muted"
                          title={`Collected for organization ${orgLabel(ch.org_slug)}`}>
                          {orgLabel(ch.org_slug)}
                        </span>
                      )}
                      {ch.owner_email && (
                        <span className="text-[10px] rounded bg-table-stripe px-1.5 py-0.5 text-page-muted"
                          title={`Personal chat shared by ${ch.owner_email}`}>
                          shared chat
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-page-muted">{ch.num_members ?? "—"}</td>
                  <td className="px-3 py-2 text-page-muted">{fmtActivity(ch.last_activity)}</td>
                  <td className="px-3 py-2"><StatusPill ch={ch} /></td>
                  <td className="px-3 py-2">
                    {ch.enabled ? (
                      <span className="text-[11px] text-page-muted">{ch.backfill_mode === "new_only" ? "new only" : "backfill ≤30d"}</span>
                    ) : (
                      <select className={selectCls}
                        value={modeDraft[ch.channel_id] || ch.backfill_mode || "backfill"}
                        onChange={(e) => setModeDraft((p) => ({ ...p, [ch.channel_id]: e.target.value }))}>
                        <option value="backfill">backfill ≤30d</option>
                        <option value="new_only">new only</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/* Personal shared chats are managed by their owner (My chats
                        tab); group toggles need org-admin rights over the
                        channel's org (server-enforced too). */}
                    <Switch checked={!!ch.enabled}
                      disabled={!ch.present || !ch.is_member || !!ch.owner_email ||
                        !(manageableOrgs === null || manageableOrgs.includes(ch.org_slug))}
                      onCheckedChange={(v) => toggleChannel(ch, v)} />
                  </td>
                </tr>
              ))}
              {channels !== null && channels.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-page-muted">
                  No channels yet — run Sync Slack once to pull the registry from Slack.
                </td></tr>
              )}
              {channels !== null && channels.length > 0 && visibleChannels.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-page-muted">
                  No channels match this filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {tab === "meetings" && (
        <div className="max-w-4xl space-y-4">
          {/* One button = ask every connected meeting service (Fireflies now,
              more later) for new calls, then reload the list itself. */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-page-muted">
              {mtSyncing ? "checking connected meeting services…" : "Upload a recording, or pull new calls from connected services."}
            </span>
            <button type="button" className={`${btnPrimary} ml-auto`} disabled={mtSyncing} onClick={syncMeetings}>
              {mtSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} strokeWidth={2.5} />}
              {mtSyncing ? "Syncing…" : "Sync meetings"}
            </button>
          </div>
          <MeetingUpload onUploaded={load} orgs={meetingOrgs} orgLabel={orgLabel} />
          {(meetings || []).map((m) => (
            <MeetingCard key={m.id} meeting={m} projects={projects} spec={spec}
              me={myEmail} orgs={meetingOrgs} orgLabel={orgLabel} isAdmin={isAdmin}
              onChanged={load}
              onDeleted={(id) => setMeetings((prev) => (prev || []).filter((x) => x.id !== id))} />
          ))}
          {meetings !== null && meetings.length === 0 && (
            <div className="text-sm text-page-muted py-8 text-center">
              No meetings yet — upload a call recording above, or connect your meeting service (Fireflies, Krisp) in Settings.
            </div>
          )}
        </div>
      )}

      {tab === "mychats" && (
        <div className="rounded-lg border border-card-border bg-card-bg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-table-header">
              <tr className="text-left text-table-header-text">
                <th className="px-3 py-2 font-medium">Chat</th>
                <th className="px-3 py-2 font-medium">Last activity</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">On enable</th>
                <th className="px-3 py-2 font-medium">Visible to everyone</th>
                <th className="px-3 py-2 font-medium text-right">Listen</th>
              </tr>
            </thead>
            <tbody>
              {myChats.map((ch, i) => (
                <tr key={`${ch.org_slug || ""}:${ch.channel_id}`} className={i % 2 ? "bg-table-stripe" : ""}>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 text-page-text">
                      <MessageCircle size={13} strokeWidth={2.5} className="text-page-muted" />
                      {ch.name}
                      {ch.ch_type === "mpim" && <span className="text-[10px] text-page-muted">(group DM)</span>}
                      {new Set(myChats.map((c) => c.org_slug)).size > 1 && ch.org_slug && (
                        <span className="text-[10px] rounded bg-table-stripe px-1.5 py-0.5 text-page-muted"
                          title={`This copy lives in organization ${orgLabel(ch.org_slug)} — listen/share apply there`}>
                          {orgLabel(ch.org_slug)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-page-muted">{fmtActivity(ch.last_activity)}</td>
                  <td className="px-3 py-2"><StatusPill ch={{ ...ch, is_member: true }} /></td>
                  <td className="px-3 py-2">
                    {ch.enabled ? (
                      <span className="text-[11px] text-page-muted">{ch.backfill_mode === "new_only" ? "new only" : "backfill ≤30d"}</span>
                    ) : (
                      <select className={selectCls}
                        value={modeDraft[ch.channel_id] || ch.backfill_mode || "backfill"}
                        onChange={(e) => setModeDraft((p) => ({ ...p, [ch.channel_id]: e.target.value }))}>
                        <option value="backfill">backfill ≤30d</option>
                        <option value="new_only">new only</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Switch checked={!!ch.shared}
                      onCheckedChange={(v) => patchMyChat(ch, { shared: v })} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Switch checked={!!ch.enabled} disabled={!ch.present}
                      onCheckedChange={(v) => patchMyChat(ch, { enabled: v, backfill_mode: modeDraft[ch.channel_id] || ch.backfill_mode || "backfill" })} />
                  </td>
                </tr>
              ))}
              {myChats.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-page-muted">
                  {myTokens.length ? "No chats synced yet — press Sync Slack or re-save the token." : "Add your Slack token on the Settings tab to see your chats here."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && (
        <div className="max-w-3xl space-y-4">
          {/* Two families of connections, one tab each: Slack tokens feed the
              CHAT side (digests, channels, my chats); meeting services feed
              Meetings. */}
          <div className="flex items-center gap-1 border-b border-card-border">
            {[["slack", "Slack — chats & channels"], ["meetings", "Meeting services"], ["privacy", "Privacy"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setParams({ st: id === "slack" ? null : id, service: null })}
                className={`px-3 py-2 text-sm font-medium bg-transparent border-0 border-b-2 -mb-px cursor-pointer ${
                  stTab === id ? "border-nav-active text-nav-active" : "border-transparent text-page-muted hover:text-page-text"}`}>
                {label}
              </button>
            ))}
          </div>
          {stTab === "slack" && (<>
          <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <KeyRound size={16} strokeWidth={2.5} className="text-nav-active" />
              <span className="text-sm font-semibold text-page-title">Personal Slack token</span>
              <TokenHelpDialog />
            </div>
            <p className="text-xs text-page-muted">
              Connect your own Slack account to add your chats to the feed. Your DMs stay visible only to you
              unless you share a specific chat; group channels you add become part of the shared registry.
            </p>
            {myTokens.map((t) => (
              <div key={t.org_slug} className="flex items-center gap-3 flex-wrap">
                {/* A multi-org caller must always see WHICH org a connection
                    belongs to — even with a single token row (otherwise "is
                    the other org connected?" has no answer on this screen). */}
                <span title={`connected to ${orgLabel(t.org_slug)}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)] px-2.5 py-1 text-xs font-medium">
                  <CircleCheck size={12} strokeWidth={2.5} />
                  {(myTokens.length > 1 || (myMemberOrgs || []).length > 1) ? `${orgLabel(t.org_slug)} · ` : ""}{t.team} · {t.slack_user_name}
                </span>
                <label className="inline-flex items-center gap-1.5 text-xs text-page-muted">
                  pulls:
                  <select className={selectCls} value={t.mode}
                    onChange={async (e) => {
                      const mode = e.target.value;
                      try {
                        const res = await fetchData("slack/token", {
                          method: "PATCH", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ mode, org_slug: t.org_slug }),
                        });
                        toast.success(`mode updated — ${res.dms} chats, ${res.groups} group channels synced`);
                        await load();
                      } catch (err) {
                        toast.error(String(err?.message || "could not change the mode"));
                      }
                    }}>
                    <option value="dm">personal chats only</option>
                    <option value="groups">group channels only</option>
                    <option value="both">personal + group</option>
                  </select>
                </label>
                <button type="button" className={`${btnGhost} ml-auto`} onClick={() => removeToken(t.org_slug)}>
                  <Trash2 size={14} strokeWidth={2.5} /> Remove token
                </button>
              </div>
            ))}
            {/* Connect form: one connection per org — the same token can be
                brought to every org the caller is a member of (each org gets
                its own copy of the chats with independent listen/share). */}
            {(() => {
              const connected = new Set(myTokens.map((t) => t.org_slug));
              // Always the backend's fresh list — the login-time user object
              // goes stale the moment an org is added mid-session.
              const memberOrgs = myMemberOrgs ?? [];
              const allOrgs = [...new Set(memberOrgs)];
              const connectable = allOrgs.filter((o) => !connected.has(o));
              if (connectable.length === 0) return null;
              const targetOrg = connectable.includes(tokenOrg) ? tokenOrg : connectable[0];
              return (
                <>
                  {myTokens.length > 0 && (
                    <div className="text-[11px] text-page-muted">Connect another organization</div>
                  )}
                  <div className="flex items-center gap-2">
                    {/* Multi-org caller ⇒ always show WHICH org this connect
                        targets, even when only one org is left to connect. */}
                    {allOrgs.length > 1 && (
                      <select className={selectCls} value={targetOrg} onChange={(e) => setTokenOrg(e.target.value)}>
                        {connectable.map((o) => <option key={o} value={o}>{orgLabel(o)}</option>)}
                      </select>
                    )}
                    <input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
                      placeholder="xoxp-…"
                      className="flex-1 rounded-md border border-page-muted/50 bg-page-bg text-page-text text-sm px-3 py-1.5 outline-none focus:border-nav-active" />
                    <button type="button" className={btnPrimary} disabled={savingToken || !tokenInput.trim()} onClick={() => saveToken(targetOrg)}>
                      {savingToken ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={2.5} />} Connect
                    </button>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-page-text">
                    {[["dm", "Personal chats only"], ["groups", "Group channels only"], ["both", "Both"]].map(([v, label]) => (
                      <label key={v} className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="slack-token-mode" value={v} checked={tokenMode === v}
                          onChange={() => setTokenMode(v)} className="accent-[var(--nav-active)]" />
                        {label}
                      </label>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
          {orgTokens !== null ? (
            <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Hash size={16} strokeWidth={2.5} className="text-nav-active" />
                <span className="text-sm font-semibold text-page-title">Organization Slack workspace</span>
                <TokenHelpDialog org />
              </div>
              <p className="text-xs text-page-muted">
                The workspace token pulls your organization's group channels for everyone (no DM scopes).
                Coverage equals the token account's channel memberships — a dedicated service account
                (e.g. cortex-bot) beats a personal one: it won't leave channels or the company.
              </p>
              {(orgTokens.tokens || []).map((t) => (
                <div key={t.org_slug} className="flex items-center gap-3 flex-wrap">
                  <span title={t.added_by ? `connected by ${t.added_by}` : undefined}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)] px-2.5 py-1 text-xs font-medium">
                    <CircleCheck size={12} strokeWidth={2.5} />
                    {orgLabel(t.org_slug)}{t.team ? ` · ${t.team}` : ""}{t.slack_user_name ? ` · via ${t.slack_user_name}` : ""}
                  </span>
                  {t.source === "env" ? (
                    <span className="text-xs text-page-muted">configured on the backend (env)</span>
                  ) : (
                    <button type="button" className={`${btnGhost} ml-auto`} onClick={async () => {
                      try {
                        await fetchData("slack/org_token", {
                          method: "DELETE", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ org_slug: t.org_slug }),
                        });
                        toast.success(`${orgLabel(t.org_slug)}: workspace disconnected`);
                        await load();
                      } catch (e) { toast.error(String(e?.message || "disconnect failed")); }
                    }}>
                      <Trash2 size={14} strokeWidth={2.5} /> Disconnect
                    </button>
                  )}
                </div>
              ))}
              {/* One token per ORG, but the same workspace token may back
                  several orgs (each org keeps its own channel registry and
                  digests). The input targets any managed org that has no db
                  token yet; replacing an org's token = Disconnect it first. */}
              {(() => {
                const connected = new Set((orgTokens.tokens || []).filter((t) => t.source === "db").map((t) => t.org_slug));
                const myAdminOrgs = [...new Set(orgTokens.adminOrgs ?? [])];
                const connectable = myAdminOrgs.filter((o) => !connected.has(o));
                if (connectable.length === 0) {
                  return <p className="text-xs text-page-muted">To replace a token, disconnect that workspace first.</p>;
                }
                const targetOrg = connectable.includes(orgTokenOrg) ? orgTokenOrg : connectable[0];
                return (
                  <div className="space-y-1.5">
                  {(orgTokens.tokens || []).length > 0 && (
                    <div className="text-[11px] text-page-muted">Connect another organization</div>
                  )}
                  <div className="flex items-center gap-2">
                    {myAdminOrgs.length > 1 && (
                      <select className={selectCls} value={targetOrg} onChange={(e) => setOrgTokenOrg(e.target.value)}>
                        {connectable.map((o) => <option key={o} value={o}>{orgLabel(o)}</option>)}
                      </select>
                    )}
                    <input type="password" value={orgTokenInput} onChange={(e) => setOrgTokenInput(e.target.value)}
                      placeholder="xoxp-… (workspace token)"
                      className="flex-1 rounded-md border border-page-muted/50 bg-page-bg text-page-text text-sm px-3 py-1.5 outline-none focus:border-nav-active" />
                    <button type="button" className={btnPrimary} disabled={savingOrgToken || !orgTokenInput.trim()}
                      onClick={async () => {
                        setSavingOrgToken(true);
                        try {
                          const res = await fetchData("slack/org_token", {
                            method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ token: orgTokenInput.trim(), org_slug: targetOrg }),
                          });
                          toast.success(`connected ${res.org_slug} → ${res.team} — press Sync Slack to pull channels`);
                          setOrgTokenInput("");
                          await load();
                        } catch (e) {
                          toast.error(String(e?.message || "could not save the workspace token"));
                        } finally { setSavingOrgToken(false); }
                      }}>
                      {savingOrgToken ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={2.5} />} Connect
                    </button>
                  </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-1">
              <div className="text-sm font-semibold text-page-title">Organization Slack workspace</div>
              <p className="text-xs text-page-muted">
                {status?.configured
                  ? "Your organization's workspace is connected — group channels are collected centrally (org admins manage them on the Channels tab)."
                  : "Your organization has no workspace token yet — ask your org admin to connect one here."}
              </p>
            </div>
          )}
          </>)}
          {stTab === "privacy" && <PrivacySettings orgLabel={orgLabel} />}
          {stTab === "meetings" && integ !== null && (() => {
            // PERSONAL connections: one per service per user (the key is yours
            // at the provider too). Imports land private to you; sharing a
            // meeting with an org is the per-meeting Share button.
            const rowOf = (kind) => (integ.integrations || []).find((it) => it.kind === kind) || null;
            const ffRow = rowOf("fireflies");
            const krRow = rowOf("krisp");
            const kaRow = rowOf("krisp_api");
            // The Feed page talks to the backend directly, so the webhook URL
            // shares the API's public origin.
            const apiBase = String(import.meta.env.VITE_BACKEND_ADDRESS || "/").replace(/\/+$/, "");
            const syncPill = (it, waitingLabel) => it.last_error ? (
              <span className="text-xs text-[var(--severity-bad-text,#b91c1c)]" title={it.last_error}>
                last sync failed
              </span>
            ) : it.last_sync_at ? (
              <span className="text-xs text-page-muted">
                synced {new Date(it.last_sync_at).toLocaleString()}
              </span>
            ) : (
              <span className="text-xs text-page-muted">{waitingLabel}</span>
            );
            // The services catalog: a Discover grid of cards, each opening its
            // own settings page (?service=…). Logos are the official marks on
            // a white tile so the dark ones survive the dark theme.
            const services = [
              { id: "fireflies", name: "Fireflies.ai", logo: "/fireflies-logo.png",
                desc: "Meeting bot for Zoom / Meet / Teams — transcripts pull in on sync.",
                connections: ffRow ? [ffRow] : [] },
              { id: "krisp", name: "Krisp", logo: "/krisp-logo.png",
                desc: "Desktop call recorder — webhook lands calls instantly, API key backfills history.",
                connections: [krRow, kaRow].filter(Boolean) },
            ];
            if (!svc) {
              const connected = services.filter((s) => s.connections.length > 0);
              return (
                <>
                  {connected.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-base font-semibold text-page-title px-1">My Integrations</h3>
                      <div className="rounded-lg border border-card-border bg-card-bg divide-y divide-card-border">
                        {connected.map((s) => (
                          <div key={s.id} className="flex items-center gap-3 px-4 py-3">
                            <span className="w-8 h-8 rounded-lg bg-white border border-card-border flex items-center justify-center shrink-0">
                              <img src={s.logo} alt="" className="w-5 h-5 object-contain" />
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-page-title">{s.name}</div>
                              <div className="text-[11px] text-page-muted truncate">
                                {s.connections.some((it) => it.last_error) ? "last sync failed" : "your personal connection"}
                              </div>
                            </div>
                            <button type="button" className={`${btnGhost} ml-auto`}
                              onClick={() => setParams({ service: s.id })}>
                              Manage
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 pt-1">
                    <h3 className="text-base font-semibold text-page-title px-1">Discover Meeting Integrations</h3>
                    <p className="text-xs text-page-muted px-1">
                      Third-party call recorders that land transcripts on the Meetings tab. The connection
                      is personal — your key, your calls — and every imported meeting is private to you
                      until you share it with an organization. Pull new calls with "Sync meetings" right
                      on that tab.
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-1">
                      {services.map((s) => (
                        <button key={s.id} type="button" data-testid={`feed-svc-${s.id}`}
                          onClick={() => setParams({ service: s.id })}
                          className="aspect-square rounded-2xl border border-card-border bg-card-bg p-4 flex flex-col items-center justify-center gap-2.5 cursor-pointer text-center hover:border-nav-active/60 transition-colors">
                          <span className="w-14 h-14 rounded-2xl bg-white border border-card-border flex items-center justify-center">
                            <img src={s.logo} alt={`${s.name} logo`} className="w-9 h-9 object-contain" />
                          </span>
                          <span className="text-sm font-semibold text-page-title">{s.name}</span>
                          <span className="text-[11px] text-page-muted leading-snug">{s.desc}</span>
                          {s.connections.length > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)] px-2 py-0.5 text-[11px] font-medium">
                              <CircleCheck size={11} strokeWidth={2.5} /> connected
                            </span>
                          ) : (
                            <span className="text-[11px] text-page-muted/70">not connected</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              );
            }
            const cur = services.find((s) => s.id === svc);
            return (
              <>
                {/* Short path back: "Meeting services → <service>". */}
                <div className="flex items-center gap-1.5 px-1">
                  <button type="button" onClick={() => setParams({ service: null })}
                    className="bg-transparent border-0 p-0 text-sm font-medium text-nav-active cursor-pointer hover:underline">
                    Meeting services
                  </button>
                  <ChevronRight size={14} className="text-page-muted" />
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-page-title">
                    <img src={cur.logo} alt="" className="w-4 h-4 object-contain" /> {cur.name}
                  </span>
                </div>
                {svc === "fireflies" && (
                <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Mic size={16} strokeWidth={2.5} className="text-nav-active" />
                    <span className="text-sm font-semibold text-page-title">Fireflies.ai</span>
                  </div>
                  <p className="text-xs text-page-muted">
                    Pull YOUR call transcripts from Fireflies into Meetings — with real speaker labels,
                    checked hourly (Fireflies' own notes are kept alongside). Imported meetings are
                    private to you until you share them. The API key lives at fireflies.ai →
                    Integrations → Fireflies API.
                  </p>
                  {ffRow ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)] px-2.5 py-1 text-xs font-medium">
                        <CircleCheck size={12} strokeWidth={2.5} />
                        key {ffRow.key_hint}
                      </span>
                      {syncPill(ffRow, "first sync pending…")}
                      <button type="button" className={`${btnGhost} ml-auto`} onClick={async () => {
                        try {
                          await fetchData("integrations/fireflies", { method: "DELETE" });
                          toast.success("Fireflies disconnected — imported meetings stay");
                          await load();
                        } catch (e) { toast.error(String(e?.message || "disconnect failed")); }
                      }}>
                        <Trash2 size={14} strokeWidth={2.5} /> Disconnect
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input type="password" value={ffKeyInput} onChange={(e) => setFfKeyInput(e.target.value)}
                        placeholder="Fireflies API key"
                        className="flex-1 rounded-md border border-page-muted/50 bg-page-bg text-page-text text-sm px-3 py-1.5 outline-none focus:border-nav-active" />
                      <button type="button" className={btnPrimary} disabled={savingFf || !ffKeyInput.trim()}
                        onClick={async () => {
                          setSavingFf(true);
                          try {
                            await fetchData("integrations/fireflies", {
                              method: "POST", headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ api_key: ffKeyInput.trim() }),
                            });
                            toast.success("Fireflies connected — pulling the last 7 days of your calls");
                            setFfKeyInput("");
                            await load();
                          } catch (e) {
                            toast.error(String(e?.message || "could not connect Fireflies"));
                          } finally { setSavingFf(false); }
                        }}>
                        {savingFf ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={2.5} />} Connect
                      </button>
                    </div>
                  )}
                </div>
                )}
                {svc === "krisp" && (
                <div className="rounded-lg border border-card-border bg-card-bg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Webhook size={16} strokeWidth={2.5} className="text-nav-active" />
                    <span className="text-sm font-semibold text-page-title">Krisp</span>
                    <KrispHelpDialog urls={krRow ? [{ org: "you", url: `${apiBase}${krRow.webhook_path}` }] : []} />
                  </div>
                  <p className="text-xs text-page-muted">
                    Two independent ways to connect, best together: the <span className="font-medium">webhook</span> lands
                    each call seconds after it ends (Krisp pushes to us), the <span className="font-medium">API key</span> pulls
                    on sync and backfills the history recorded before the webhook existed. Both write
                    the same meetings — duplicates merge. Everything imports private to you until you
                    share it. Summaries are written on our side, same as for manual uploads. Click the
                    info icon for the step-by-step.
                  </p>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted pt-1">
                    Webhook — instant delivery
                  </div>
                  {krRow ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)] px-2.5 py-1 text-xs font-medium">
                          <CircleCheck size={12} strokeWidth={2.5} />
                          your webhook
                        </span>
                        {syncPill(krRow, "waiting for the first delivery…")}
                        <button type="button" className={`${btnGhost} ml-auto`} onClick={async () => {
                          try {
                            await fetchData("integrations/krisp", { method: "DELETE" });
                            toast.success("Krisp disconnected — the webhook URL stops working, imported meetings stay");
                            await load();
                          } catch (e) { toast.error(String(e?.message || "disconnect failed")); }
                        }}>
                          <Trash2 size={14} strokeWidth={2.5} /> Disconnect
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <input readOnly value={`${apiBase}${krRow.webhook_path}`}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 rounded-md border border-page-muted/50 bg-page-bg text-page-muted text-xs px-3 py-1.5 outline-none font-mono" />
                        <button type="button" className={btnGhost} onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`${apiBase}${krRow.webhook_path}`);
                            toast.success("webhook URL copied");
                          } catch { toast.error("could not copy — select the URL manually"); }
                        }}>
                          <Copy size={14} strokeWidth={2.5} /> Copy
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className={btnPrimary} disabled={savingKrisp} onClick={async () => {
                      setSavingKrisp(true);
                      try {
                        await fetchData("integrations/krisp", { method: "POST" });
                        toast.success("webhook created — paste the URL into Krisp → Settings → Integrations");
                        await load();
                      } catch (e) {
                        toast.error(String(e?.message || "could not connect Krisp"));
                      } finally { setSavingKrisp(false); }
                    }}>
                      {savingKrisp ? <Loader2 size={14} className="animate-spin" /> : <Webhook size={14} strokeWidth={2.5} />}
                      Generate webhook URL
                    </button>
                  )}
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted pt-2">
                    API key — sync &amp; history backfill
                  </div>
                  {kaRow ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full bg-[var(--severity-ok-bg,#dcfce7)] text-[var(--severity-ok-text,#166534)] px-2.5 py-1 text-xs font-medium">
                        <CircleCheck size={12} strokeWidth={2.5} />
                        key {kaRow.key_hint}
                      </span>
                      {syncPill(kaRow, "first sync pending…")}
                      <button type="button" className={`${btnGhost} ml-auto`} onClick={async () => {
                        try {
                          await fetchData("integrations/krisp_api", { method: "DELETE" });
                          toast.success("Krisp API key disconnected — imported meetings stay");
                          await load();
                        } catch (e) { toast.error(String(e?.message || "disconnect failed")); }
                      }}>
                        <Trash2 size={14} strokeWidth={2.5} /> Disconnect
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input type="password" value={kaKeyInput} onChange={(e) => setKaKeyInput(e.target.value)}
                        placeholder="krsp_u_… (Krisp → Settings → Integrations → API)"
                        className="flex-1 rounded-md border border-page-muted/50 bg-page-bg text-page-text text-sm px-3 py-1.5 outline-none focus:border-nav-active" />
                      <button type="button" className={btnPrimary} disabled={savingKa || !kaKeyInput.trim()}
                        onClick={async () => {
                          setSavingKa(true);
                          try {
                            await fetchData("integrations/krisp_api", {
                              method: "POST", headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ api_key: kaKeyInput.trim() }),
                            });
                            toast.success("Krisp API key connected — pulling the last 90 days of your calls");
                            setKaKeyInput("");
                            await load();
                          } catch (e) {
                            toast.error(String(e?.message || "could not connect the Krisp API key"));
                          } finally { setSavingKa(false); }
                        }}>
                        {savingKa ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={2.5} />} Connect
                      </button>
                    </div>
                  )}
                </div>
                )}
              </>
            );
          })()}
        </div>
      )}
      </div>
      </main>
    </div>
  );
}
